/**
 * Comprehensive skills dataset for the /skills library.
 *
 * Each entry describes an agent-oriented use case: what it does, which PostHog
 * products/features power it (`tools_used`), and the ordered chain of PostHog
 * MCP tools an agent would call (`flow`). `stage` is the company stage the use
 * case fits best (MVP → Enterprise); `tags` are the departments/personas
 * it serves (a skill can serve several).
 *
 * `example_prompts` are illustrative user asks. They lean on PostHog's current
 * capabilities — including PostHog Desktop (our AI editor that opens PRs), Signals,
 * and cross-product chains — so they show how skills and tools combine into real
 * outcomes, not just read-only analytics.
 *
 * This is the curated marketing layer. The canonical agent skills live in the
 * monorepo (`products/<product>/skills/<name>/SKILL.md`) and are ingested at build time as
 * `AgentSkill` nodes — see gatsby/onCreateNode.ts and hooks/skills.tsx.
 */

export type IncomingSkill = {
    name: string
    /** Company stage: MVP | Startup | Scale | Enterprise */
    stage: string
    /** Departments / personas this serves (also the department dimension) */
    tags: string[]
    description: string
    /** Comma-delimited product/feature names — split + linked to product handles */
    tools_used: string
    /** Comma-delimited PostHog MCP tool handles, in call order */
    flow: string
    example_prompts: string[]
}

export const skillsData: IncomingSkill[] = [
    {
        name: 'Debug a user complaint',
        stage: 'Startup',
        tags: ['Support', 'Engineering'],
        description:
            'Investigates recent activity for a user with an emphasis on errors or warnings to determine what went wrong in a user session',
        tools_used: 'Session Replay, Logs, Error Tracking, Activity log, Product Analytics',
        flow: 'persons-list, persons-retrieve, query-session-recordings-list, session-recording-get, session-recording-summarize, query-logs, query-error-tracking-issues-list',
        example_prompts: [
            'Figure out what mary@acme.com is complaining about and summarize what broke',
            "Why did this user's checkout fail yesterday? Pull their session and the logs around it",
            'Find the error behind this ticket and have PostHog Desktop open a PR with the fix',
        ],
    },
    {
        name: 'Identify top errors today',
        stage: 'Scale',
        tags: ['Engineering', 'Ops'],
        description: 'Surfaces the highest-volume error tracking issues in the last 24h and ranks by affected users',
        tools_used: 'Error Tracking, Product Analytics',
        flow: 'query-error-tracking-issues-list, query-error-tracking-issue, query-error-tracking-issue-events',
        example_prompts: [
            "What are today's top errors, ranked by how many users they hit?",
            'Show me the worst regression since this morning and who it affects',
            'Triage the top 3 issues and open PRs for the ones with an obvious fix',
        ],
    },
    {
        name: 'Monitor a competitor',
        stage: 'Scale',
        tags: ['Marketing', 'Founder', 'Product Management'],
        description:
            "Tracks a competitor's pricing, positioning, and feature shifts and writes findings to a notebook for the team",
        tools_used: 'Notebooks, Annotations, Web Analytics',
        flow: 'notebooks-create, notebook-edit, annotation-create',
        example_prompts: [
            'Track what changed on our top competitor’s pricing page and log it in a notebook',
            'Start a competitor watch notebook and add an annotation when they launch something',
            'Summarize this competitor’s positioning shift this quarter for the team',
        ],
    },
    {
        name: 'Create a changelog from shipped features',
        stage: 'Scale',
        tags: ['Marketing', 'Product Management', 'Engineering'],
        description:
            'Looks at recently rolled-out feature flags and experiments and drafts a customer-facing changelog',
        tools_used: 'Feature Flags, Experiments, Notebooks',
        flow: 'feature-flag-get-all, experiment-list, notebooks-create, notebook-edit',
        example_prompts: [
            'Draft this month’s changelog from the flags and experiments we shipped',
            'Which features went to 100% this sprint? Turn them into release notes',
            'Write a customer-facing changelog and drop it in a notebook for review',
        ],
    },
    {
        name: 'Reduce churn',
        stage: 'Scale',
        tags: ['Product Management', 'Founder', 'Growth'],
        description:
            'Identifies cohorts of users who churned in the last 30 days and analyzes their last-session behavior to recommend interventions',
        tools_used: 'Cohorts, Session Replay, Product Analytics, Surveys',
        flow: 'cohorts-create, query-retention, query-session-recordings-list, session-recording-summarize, survey-create',
        example_prompts: [
            'Build a cohort of users who churned last month and tell me what they did right before leaving',
            'Watch the last sessions of churned users and find the common drop-off',
            'Spin up a cancel survey for the churn cohort and summarize the reasons',
        ],
    },
    {
        name: 'Monitor adoption of a new feature',
        stage: 'Scale',
        tags: ['Product Management', 'Engineering'],
        description: 'Tracks daily active users of a feature against rollout %, broken down by plan and country',
        tools_used: 'Feature Flags, Product Analytics, Dashboards',
        flow: 'feature-flag-get-definition, query-trends, dashboard-create, insight-create',
        example_prompts: [
            'Track DAU of the new editor against its rollout %, split by plan',
            'Is adoption of the export feature keeping up as we ramp the flag?',
            'Build a dashboard for new-feature adoption by country',
        ],
    },
    {
        name: 'Forecast growth',
        stage: 'Startup',
        tags: ['Founder', 'Growth', 'Data'],
        description:
            'Models projected MAU/MRR over the next two quarters from current trends and historical seasonality',
        tools_used: 'Product Analytics, SQL, Dashboards',
        flow: 'query-trends, execute-sql, insight-create, dashboard-create',
        example_prompts: [
            'Project our MAU and MRR for the next two quarters from current trends',
            'Model growth with seasonality and pin it to a dashboard',
            'What’s our trajectory if the last 8 weeks hold?',
        ],
    },
    {
        name: 'Identify errors after a deploy',
        stage: 'Scale',
        tags: ['Engineering', 'Ops'],
        description: 'Compares error volume before and after a deploy annotation and surfaces newly introduced issues',
        tools_used: 'Error Tracking, Annotations, Logs',
        flow: 'annotations-list, query-error-tracking-issues-list, query-logs',
        example_prompts: [
            'Did the 2pm deploy introduce any new errors? Compare before vs after the annotation',
            'Surface issues that only started after our last release',
            'If the deploy caused this spike, open a revert PR with PostHog Desktop',
        ],
    },
    {
        name: 'Triage support tickets by impact',
        stage: 'Scale',
        tags: ['Support', 'Ops'],
        description: 'Pulls open conversations and ranks them by user revenue and number of similar reports',
        tools_used: 'Support, Group Analytics, Product Analytics',
        flow: 'conversations-tickets-list, conversations-tickets-retrieve, persons-retrieve, query-run',
        example_prompts: [
            'Rank open tickets by account revenue and how many people reported the same thing',
            'Which support tickets should I answer first today?',
            'Group duplicate reports and tell me the highest-impact one',
        ],
    },
    {
        name: 'Find rage-click hotspots',
        stage: 'Startup',
        tags: ['Design', 'Product Management', 'Support'],
        description: 'Locates pages with high rage-click frequency and pulls representative replays for design review',
        tools_used: 'Heatmaps, Session Replay, Web Analytics',
        flow: 'query-session-recordings-list, session-recording-get, session-recording-summarize',
        example_prompts: [
            'Where are users rage-clicking the most? Pull a few replays for design review',
            'Find the most frustrating page this week and show me why',
            'Summarize what users are trying to do when they rage-click /settings',
        ],
    },
    {
        name: 'Audit feature flag rollouts',
        stage: 'Enterprise',
        tags: ['Engineering', 'Security', 'Ops'],
        description:
            'Reviews flag changes over the past week with reasoning and rollback links for a release governance review',
        tools_used: 'Feature Flags, Activity log',
        flow: 'feature-flag-get-all, feature-flags-activity-retrieve, activity-log-list',
        example_prompts: [
            'List every flag change this week with who made it and a rollback link',
            'Prep a release-governance review of our flag activity',
            'Which flags changed without a corresponding ticket?',
        ],
    },
    {
        name: 'Investigate a checkout dropoff',
        stage: 'Scale',
        tags: ['Product Management', 'Growth', 'Engineering'],
        description:
            'Builds a funnel for checkout, identifies the biggest dropoff step, and pulls replays of users who failed it',
        tools_used: 'Product Analytics, Session Replay',
        flow: 'query-funnel, query-session-recordings-list, session-recording-summarize',
        example_prompts: [
            'Build the checkout funnel and show me the biggest drop-off step',
            'Pull replays of users who failed at payment and tell me what went wrong',
            'Why are people abandoning checkout on mobile?',
        ],
    },
    {
        name: 'Score lead accounts',
        stage: 'Scale',
        tags: ['Sales', 'Growth', 'Data'],
        description: 'Combines event activity, plan, and team size to rank accounts most likely to convert',
        tools_used: 'Group Analytics, SQL, Cohorts',
        flow: 'accounts-list, execute-sql, cohorts-create',
        example_prompts: [
            'Rank our accounts by likelihood to convert using usage, plan, and team size',
            'Build a PQL cohort from the highest-scoring accounts',
            'Which trials look most like our best customers?',
        ],
    },
    {
        name: 'Detect failed payments',
        stage: 'Scale',
        tags: ['Billing', 'Finance', 'Ops'],
        description:
            'Surfaces failed-payment events from the last 7 days, groups by reason code, and identifies recoverable cohorts',
        tools_used: 'Product Analytics, Cohorts, CDP',
        flow: 'query-trends, cohorts-create, cdp-functions-list',
        example_prompts: [
            'Show failed payments this week grouped by decline reason',
            'Build a cohort of recoverable failed payments and wire up a dunning function',
            'Which card declines are worth retrying?',
        ],
    },
    {
        name: 'Spin up a prototype event taxonomy',
        stage: 'MVP',
        tags: ['Engineering', 'Data', 'Product Management'],
        description: 'Defines a starter set of events and properties for a brand-new product and validates ingestion',
        tools_used: 'Events, Properties, Data Schema',
        flow: 'event-definitions-list, properties-list, read-data-schema, event-definition-update',
        example_prompts: [
            'Propose a starter event taxonomy for this new product and check ingestion',
            'What events and properties should we track for an MVP signup flow?',
            'Validate that the events I just added are arriving with the right properties',
        ],
    },
    {
        name: 'Spin up an MVP dashboard',
        stage: 'MVP',
        tags: ['Founder', 'Product Management'],
        description:
            'Builds a starter dashboard with signups, activation, retention, and weekly actives for a new product',
        tools_used: 'Dashboards, Product Analytics',
        flow: 'insight-create, dashboard-create, query-trends, query-retention',
        example_prompts: [
            'Build me a starter dashboard: signups, activation, retention, weekly actives',
            'Set up the four metrics I should watch for my MVP',
            'Make a first dashboard and pin it to my home',
        ],
    },
    {
        name: 'Set up product-market-fit survey',
        stage: 'Startup',
        tags: ['Product Management', 'Founder', 'Growth'],
        description: 'Creates the Sean Ellis PMF survey, targets engaged users, and aggregates responses',
        tools_used: 'Surveys, Cohorts',
        flow: 'cohorts-create, survey-create, survey-stats',
        example_prompts: [
            'Launch the Sean Ellis PMF survey to engaged users and report the score',
            'Build a cohort of activated users and ask them the “how disappointed” question',
            'Aggregate our PMF survey responses by segment',
        ],
    },
    {
        name: 'Find power users',
        stage: 'Startup',
        tags: ['Product Management', 'Growth', 'Sales'],
        description: 'Builds a cohort of users in the top 5% of weekly events and surfaces their common behaviors',
        tools_used: 'Cohorts, Product Analytics',
        flow: 'cohorts-create, query-trends, persons-list',
        example_prompts: [
            'Build a cohort of our top 5% most active users and tell me what they have in common',
            'Who are our power users and which features do they all use?',
            'List power users so sales can reach out',
        ],
    },
    {
        name: 'Run an A/B test on the homepage',
        stage: 'Scale',
        tags: ['Marketing', 'Growth', 'Engineering'],
        description:
            'Designs, launches, and analyzes a homepage hero variant experiment with conversion as the primary metric',
        tools_used: 'Experiments, Feature Flags, Web Analytics',
        flow: 'experiment-create, experiment-launch, experiment-results-get, experiment-stats',
        example_prompts: [
            'Set up and launch a homepage hero A/B test with signup as the primary metric',
            'Is the new hero winning yet? Pull the experiment results',
            'Design a homepage experiment and tell me when it reaches significance',
        ],
    },
    {
        name: 'Investigate a single LLM trace',
        stage: 'Scale',
        tags: ['Engineering', 'Product Management'],
        description: 'Looks up a specific LLM trace by id, inspects spans, cost, and latency, and recommends fixes',
        tools_used: 'LLM Observability, Tracing',
        flow: 'query-llm-trace, query-llm-traces-list, apm-trace-get',
        example_prompts: [
            'Open this LLM trace and tell me where the cost and latency went',
            'Why did this agent run go wrong? Walk the spans',
            'Inspect this trace and suggest a prompt or tool-call fix',
        ],
    },
    {
        name: 'Find runaway LLM costs',
        stage: 'Scale',
        tags: ['Engineering', 'Finance', 'Ops'],
        description: 'Identifies which LLM prompts or users are driving the most spend and where to optimize',
        tools_used: 'LLM Observability',
        flow: 'get-llm-total-costs-for-project, query-llm-traces-list, llma-personal-spend',
        example_prompts: [
            'Which prompts or users are driving most of our LLM spend this month?',
            'Where can we cut LLM cost without hurting quality?',
            'Show our token spend trajectory and the top offenders',
        ],
    },
    {
        name: 'Cluster LLM interactions',
        stage: 'Scale',
        tags: ['Engineering', 'Product Management', 'Data'],
        description: 'Runs a clustering job over LLM traces to find common user intents and bad responses',
        tools_used: 'LLM Observability',
        flow: 'llma-clustering-job-list, llma-clustering-job-get, llma-evaluation-create',
        example_prompts: [
            'Cluster our LLM traces and tell me the most common user intents',
            'Find clusters of bad responses we should add evals for',
            'What are people actually asking our assistant?',
        ],
    },
    {
        name: 'Build a weekly executive digest',
        stage: 'Scale',
        tags: ['Founder', 'Product Management', 'Growth'],
        description: 'Compiles a Monday-morning digest covering growth, retention, errors, and shipped experiments',
        tools_used: 'Web Analytics, Notebooks, Dashboards',
        flow: 'web-analytics-weekly-digest, notebooks-create, dashboard-get',
        example_prompts: [
            'Write me a Monday exec digest: growth, retention, errors, and what shipped',
            'Summarize last week across the business into one notebook',
            'What moved this week and what should I worry about?',
        ],
    },
    {
        name: 'Find users blocked at onboarding',
        stage: 'Startup',
        tags: ['Support', 'Product Management', 'Growth'],
        description:
            'Finds users who started signup but never reached activation and pulls replays for the longest stuck',
        tools_used: 'Product Analytics, Session Replay, Cohorts',
        flow: 'query-funnel, cohorts-create, query-session-recordings-list, session-recording-summarize',
        example_prompts: [
            'Who started onboarding but never activated? Pull replays of the most stuck',
            'Build a cohort of users blocked in signup and tell me where they get stuck',
            'Summarize why first-time users aren’t reaching the aha moment',
        ],
    },
    {
        name: 'Audit data warehouse syncs',
        stage: 'Enterprise',
        tags: ['Data', 'Ops', 'Engineering'],
        description:
            'Checks the health of external data sources, surfaces failed syncs, and triggers reloads where safe',
        tools_used: 'Data Warehouse',
        flow: 'external-data-sources-list, external-data-sources-jobs, external-data-sync-logs, external-data-sources-reload',
        example_prompts: [
            'Which warehouse syncs are failing, and which are safe to reload?',
            'Audit our external data sources and fix the broken Stripe sync',
            'Show me sync health across all sources',
        ],
    },
    {
        name: 'Detect anomalies in event volume',
        stage: 'Scale',
        tags: ['Engineering', 'Data', 'Ops'],
        description: 'Watches incoming event volume per event name and alerts when daily counts deviate from baseline',
        tools_used: 'Alerts, Product Analytics',
        flow: 'query-trends, alert-create, alerts-list',
        example_prompts: [
            'Alert me when any event’s daily volume deviates from its baseline',
            'Is any event suddenly firing way more or less than usual?',
            'Set up anomaly alerts on our top events',
        ],
    },
    {
        name: 'Track SDK adoption across customers',
        stage: 'Enterprise',
        tags: ['Engineering', 'Product Management', 'Sales'],
        description: 'Looks at SDK versions in use across paying accounts to flag stragglers needing upgrade outreach',
        tools_used: 'Group Analytics, SDK Doctor',
        flow: 'sdk-doctor-get, accounts-list, query-run',
        example_prompts: [
            'Which paying accounts are on old SDK versions and need an upgrade nudge?',
            'Map SDK versions in the wild across our customers',
            'Flag accounts stuck on a deprecated SDK',
        ],
    },
    {
        name: 'Validate a tracking implementation',
        stage: 'MVP',
        tags: ['Engineering', 'Data'],
        description: 'Runs the SDK doctor against a new install to verify events fire correctly',
        tools_used: 'SDK Doctor, Events',
        flow: 'sdk-doctor-get, event-definitions-list, properties-list',
        example_prompts: [
            'Run the SDK doctor on my new install and tell me what’s misconfigured',
            'Are my events firing correctly end-to-end?',
            'Check my tracking setup before we launch',
        ],
    },
    {
        name: 'Find expansion-ready accounts',
        stage: 'Scale',
        tags: ['Sales', 'Growth', 'Founder'],
        description: 'Identifies accounts that have hit usage thresholds or repeatedly used premium features',
        tools_used: 'Group Analytics, Cohorts, Feature Flags',
        flow: 'accounts-list, cohorts-create, query-trends',
        example_prompts: [
            'Which accounts are hitting usage limits or leaning on premium features?',
            'Build an expansion cohort for the sales team',
            'Who’s ready for an upgrade conversation?',
        ],
    },
    {
        name: 'Find dormant high-value users',
        stage: 'Scale',
        tags: ['Sales', 'Growth', 'Marketing'],
        description:
            'Surfaces users on paid plans who have stopped using the product to trigger re-engagement campaigns',
        tools_used: 'Cohorts, Product Analytics, CDP',
        flow: 'cohorts-create, query-trends, cdp-functions-create',
        example_prompts: [
            'Find paying users who’ve gone quiet and wire up a re-engagement send',
            'Build a dormant high-value cohort and push it to our email tool',
            'Which paid accounts stopped logging in this month?',
        ],
    },
    {
        name: 'Roll back a bad feature flag',
        stage: 'Scale',
        tags: ['Engineering', 'Ops'],
        description: 'Identifies a regressing flag from error spikes and disables it',
        tools_used: 'Feature Flags, Error Tracking',
        flow: 'query-error-tracking-issues-list, feature-flag-get-definition, update-feature-flag',
        example_prompts: [
            'An error just spiked — find the flag that caused it and turn it off',
            'Which recently changed flag is behind this regression? Disable it',
            'Roll back whatever broke checkout',
        ],
    },
    {
        name: 'Compare two cohorts head to head',
        stage: 'Scale',
        tags: ['Product Management', 'Data', 'Growth'],
        description: 'Compares activation, retention, and revenue between two user cohorts to test a hypothesis',
        tools_used: 'Cohorts, Product Analytics',
        flow: 'cohorts-create, query-trends, query-retention',
        example_prompts: [
            'Compare activation and retention between mobile and desktop signups',
            'Do users who try templates retain better? Compare the two cohorts',
            'Test my hypothesis that referred users are worth more',
        ],
    },
    {
        name: 'Find the most painful page',
        stage: 'Scale',
        tags: ['Design', 'Product Management', 'Support'],
        description:
            'Ranks pages by combination of dead clicks, errors, and dropoff and recommends investigation order',
        tools_used: 'Web Analytics, Session Replay, Error Tracking',
        flow: 'query-trends, query-session-recordings-list, query-error-tracking-issues-list',
        example_prompts: [
            'Rank pages by dead clicks, errors, and drop-off and tell me what to fix first',
            'What’s our most painful page right now?',
            'Find the page hurting conversion the most and show me why',
        ],
    },
    {
        name: 'Annotate a release',
        stage: 'MVP',
        tags: ['Engineering', 'Product Management'],
        description: 'Adds a release annotation across relevant dashboards so future analysis can attribute changes',
        tools_used: 'Annotations, Dashboards',
        flow: 'annotation-create, dashboards-get-all, dashboard-update',
        example_prompts: [
            'Drop a release annotation so we can attribute metric changes to today’s deploy',
            'Mark v2.0 across our key dashboards',
            'Annotate the launch on the growth dashboard',
        ],
    },
    {
        name: 'Investigate a single trace span tree',
        stage: 'Enterprise',
        tags: ['Engineering', 'Ops'],
        description: 'Pulls the full span tree of a slow request and identifies the bottleneck service',
        tools_used: 'APM/Tracing',
        flow: 'apm-services-list, apm-spans-aggregate, apm-spans-tree, apm-trace-get',
        example_prompts: [
            'Pull the span tree for this slow request and find the bottleneck service',
            'Why is this endpoint slow? Walk the trace',
            'Which downstream call is dominating latency here?',
        ],
    },
    {
        name: 'Compare APM latency across services',
        stage: 'Enterprise',
        tags: ['Engineering', 'Ops'],
        description: 'Builds a service-by-service latency comparison and highlights the slowest path',
        tools_used: 'APM/Tracing',
        flow: 'apm-services-list, apm-attributes-list, apm-spans-aggregate',
        example_prompts: [
            'Compare p95 latency across services and highlight the slowest path',
            'Which service got slower this week?',
            'Build a service-by-service latency breakdown',
        ],
    },
    {
        name: 'Search logs for a known error string',
        stage: 'Scale',
        tags: ['Engineering', 'Support', 'Ops'],
        description: 'Finds occurrences of a known message across services and time and links sessions',
        tools_used: 'Logs',
        flow: 'logs-attributes-list, logs-attribute-values-list, query-logs, logs-count',
        example_prompts: [
            'Find every occurrence of “connection reset” across services this week',
            'Count how often this error message shows up and where',
            'Search logs for this string and link the affected sessions',
        ],
    },
    {
        name: 'Spin up a logs alert',
        stage: 'Scale',
        tags: ['Engineering', 'Ops'],
        description: 'Creates a logs alert tied to a Slack/webhook destination for a critical error pattern',
        tools_used: 'Logs, Alerts',
        flow: 'logs-alerts-create, logs-alerts-destinations-create, logs-alerts-simulate-create',
        example_prompts: [
            'Alert our Slack channel whenever this error pattern appears in logs',
            'Set up a logs alert for payment failures and test it',
            'Page on-call if this critical message shows up',
        ],
    },
    {
        name: 'Spin up a metric alert',
        stage: 'Scale',
        tags: ['Engineering', 'Ops', 'Product Management'],
        description: 'Creates a threshold or anomaly alert on a primary insight',
        tools_used: 'Alerts, Product Analytics',
        flow: 'insight-create, alert-create, alert-simulate',
        example_prompts: [
            'Alert me if signups drop below 100/day',
            'Put an anomaly alert on our activation rate',
            'Create a threshold alert on this insight and simulate it',
        ],
    },
    {
        name: 'Build a paths analysis on signup',
        stage: 'Startup',
        tags: ['Product Management', 'Growth'],
        description: 'Visualizes the most common paths users take after landing on the homepage',
        tools_used: 'Product Analytics',
        flow: 'query-paths, insight-create',
        example_prompts: [
            'Show the most common paths users take after hitting the homepage',
            'Where do people go right after signing up?',
            'Build a paths insight from landing to activation',
        ],
    },
    {
        name: 'Build a lifecycle insight',
        stage: 'Scale',
        tags: ['Product Management', 'Growth'],
        description: 'Splits actives into new/returning/resurrecting/dormant and tracks balance week over week',
        tools_used: 'Product Analytics',
        flow: 'query-lifecycle, query-lifecycle-actors, insight-create',
        example_prompts: [
            'Break our actives into new, returning, resurrecting, and dormant',
            'Is our growth healthy or are we just churning and replacing users?',
            'Build a lifecycle insight and track it week over week',
        ],
    },
    {
        name: 'Build a stickiness insight',
        stage: 'Scale',
        tags: ['Product Management', 'Growth'],
        description: 'Measures how many days per week users perform a key action',
        tools_used: 'Product Analytics',
        flow: 'query-stickiness, insight-create',
        example_prompts: [
            'How many days a week do users actually use the core feature?',
            'Build a stickiness insight for our key action',
            'Are power users sticky or just heavy on one day?',
        ],
    },
    {
        name: 'Bulk update feature flags',
        stage: 'Enterprise',
        tags: ['Engineering', 'Ops'],
        description: 'Applies tag or owner updates across many flags during a team migration',
        tools_used: 'Feature Flags',
        flow: 'feature-flag-get-all, feature-flags-bulk-update-tags-create, feature-flags-bulk-delete-create',
        example_prompts: [
            'Reassign all flags owned by the offboarding team to platform',
            'Bulk-tag every checkout flag with “payments”',
            'Clean up flags during our team migration',
        ],
    },
    {
        name: 'Find duplicate or stale flags',
        stage: 'Enterprise',
        tags: ['Engineering', 'Ops'],
        description: 'Lists flags untouched in 6+ months or with 100% rollout and recommends cleanup',
        tools_used: 'Feature Flags',
        flow: 'feature-flag-get-all, feature-flags-status-retrieve, feature-flags-bulk-delete-create',
        example_prompts: [
            'List flags at 100% rollout or untouched for 6 months and propose cleanup',
            'Which flags are safe to remove from the code?',
            'Find stale flags and open a cleanup PR with PostHog Desktop',
        ],
    },
    {
        name: 'Diagnose a slow query',
        stage: 'Enterprise',
        tags: ['Engineering', 'Data'],
        description: 'Validates and explains a HogQL query to find performance issues',
        tools_used: 'SQL, Insights',
        flow: 'hogql-schema, query-validate, query-generate-hogql-from-question, execute-sql',
        example_prompts: [
            'This query is slow — validate it and tell me how to speed it up',
            'Explain why this HogQL times out and rewrite it',
            'Optimize this query against the schema',
        ],
    },
    {
        name: 'Translate a question into HogQL',
        stage: 'Scale',
        tags: ['Data', 'Product Management'],
        description: 'Converts a plain-English question into a HogQL query for self-serve analysts',
        tools_used: 'SQL',
        flow: 'query-generate-hogql-from-question, query-validate, execute-sql',
        example_prompts: [
            'Turn “how many teams invited a second user last month” into HogQL and run it',
            'Write the SQL for this question and validate it before I share it',
            'Answer this in HogQL: revenue by acquisition channel',
        ],
    },
    {
        name: 'Build a CDP function for Slack alerts',
        stage: 'Scale',
        tags: ['Engineering', 'Ops', 'Marketing'],
        description: 'Pipes high-intent events into a Slack channel via a CDP function',
        tools_used: 'CDP',
        flow: 'cdp-function-templates-list, cdp-functions-create, cdp-functions-invocations-create',
        example_prompts: [
            'Post to #sales-signals whenever an enterprise account hits the pricing page',
            'Wire high-intent events into Slack with a CDP function',
            'Build and test a Slack alert for new paid signups',
        ],
    },
    {
        name: 'Monitor a CDP destination',
        stage: 'Scale',
        tags: ['Ops', 'Engineering'],
        description: 'Checks logs and metrics of a configured destination after a customer complaint',
        tools_used: 'CDP',
        flow: 'cdp-functions-list, cdp-functions-logs-retrieve, cdp-functions-metrics-retrieve',
        example_prompts: [
            'Is our HubSpot destination delivering? Check its logs and metrics',
            'A customer says events aren’t reaching their warehouse — diagnose the destination',
            'Show failures on the Slack destination this week',
        ],
    },
    {
        name: 'Create a workflow for trial users',
        stage: 'Scale',
        tags: ['Marketing', 'Growth'],
        description: 'Designs a multi-step workflow to nurture trial signups based on behavior',
        tools_used: 'Workflows, Cohorts',
        flow: 'cohorts-create, workflows-list, hog-flows-metrics-retrieve, hog-flows-logs-retrieve',
        example_prompts: [
            'Design a behavior-based nurture workflow for new trials',
            'Build a trial onboarding sequence and tell me how it’s performing',
            'Set up a workflow that nudges trials who haven’t activated',
        ],
    },
    {
        name: 'Schedule a flag change',
        stage: 'Scale',
        tags: ['Engineering', 'Ops'],
        description: 'Schedules a flag rollout increase to fire at a specific time during business hours',
        tools_used: 'Feature Flags',
        flow: 'feature-flag-get-definition, scheduled-changes-create, scheduled-changes-list',
        example_prompts: [
            'Schedule the new-checkout flag to go to 50% at 9am Monday',
            'Ramp this flag during business hours, not overnight',
            'Queue a rollout increase for tomorrow morning',
        ],
    },
    {
        name: 'Find users on trial about to convert',
        stage: 'Scale',
        tags: ['Sales', 'Billing', 'Growth'],
        description: 'Cohort of trial users who have hit the activation threshold and are within 3 days of trial end',
        tools_used: 'Cohorts, Group Analytics',
        flow: 'accounts-list, cohorts-create, query-trends',
        example_prompts: [
            'Build a cohort of activated trials within 3 days of expiring',
            'Who should sales call before their trial ends?',
            'Find trials that are clearly going to convert',
        ],
    },
    {
        name: 'Audit compliance access',
        stage: 'Enterprise',
        tags: ['Security', 'Ops'],
        description: 'Pulls advanced activity logs across the org for a SOC 2 access review',
        tools_used: 'Activity log',
        flow: 'advanced-activity-logs-filters, advanced-activity-logs-list, org-members-list',
        example_prompts: [
            'Pull org-wide access changes for our SOC 2 review',
            'Who accessed sensitive projects this quarter?',
            'Build the access-review evidence pack',
        ],
    },
    {
        name: 'Review change requests',
        stage: 'Enterprise',
        tags: ['Security', 'Ops', 'Engineering'],
        description: 'Lists outstanding change requests, summarizes risk, and recommends approvals',
        tools_used: 'Activity log, Approvals',
        flow: 'change-requests-list, change-request-get, approval-policies-list',
        example_prompts: [
            'List open change requests and rank them by risk',
            'Which pending changes are safe to approve?',
            'Summarize this change request and its rollback path',
        ],
    },
    {
        name: 'Review pending approvals',
        stage: 'Enterprise',
        tags: ['Security', 'Ops'],
        description: 'Walks approval policies and lists items pending action',
        tools_used: 'Approvals',
        flow: 'approval-policies-list, approval-policy-get, change-requests-list',
        example_prompts: [
            'What’s waiting on my approval right now?',
            'Walk our approval policies and list everything pending',
            'Show items blocked on an approver',
        ],
    },
    {
        name: 'Discover all dashboards in an org',
        stage: 'Enterprise',
        tags: ['Ops', 'Product Management'],
        description: 'Catalogs dashboards across projects, identifies owners, and flags abandoned ones',
        tools_used: 'Dashboards',
        flow: 'dashboards-get-all, dashboard-get, org-members-list',
        example_prompts: [
            'Catalog every dashboard across projects with its owner',
            'Which dashboards look abandoned?',
            'Find duplicate dashboards we can consolidate',
        ],
    },
    {
        name: 'Reorganize a dashboard for an exec review',
        stage: 'Scale',
        tags: ['Founder', 'Product Management'],
        description: 'Reorders tiles and updates text tiles to tell a clearer story for an exec readout',
        tools_used: 'Dashboards',
        flow: 'dashboard-get, dashboard-reorder-tiles, dashboard-update-text-tile',
        example_prompts: [
            'Reorder this dashboard so it tells a clear story for the board',
            'Add context text tiles and put the north-star metric first',
            'Tidy this dashboard up for tomorrow’s exec review',
        ],
    },
    {
        name: 'Identify churn-risk accounts',
        stage: 'Scale',
        tags: ['Sales', 'Growth', 'Support'],
        description: 'Surfaces accounts whose usage trend is sharply down and pulls recent support tickets',
        tools_used: 'Group Analytics, Product Analytics, Support',
        flow: 'accounts-list, query-trends, conversations-tickets-list',
        example_prompts: [
            'Which accounts have falling usage and open tickets? Flag churn risk',
            'Build a risk list for the CS team this week',
            'Who’s trending down and frustrated?',
        ],
    },
    {
        name: 'Compute MRR breakdown',
        stage: 'Scale',
        tags: ['Finance', 'Founder', 'Billing'],
        description: 'Breaks down MRR by plan, region, and cohort using a saved SQL query',
        tools_used: 'SQL, Data Warehouse',
        flow: 'read-data-warehouse-schema, execute-sql, insight-create',
        example_prompts: [
            'Break down MRR by plan, region, and acquisition cohort',
            'Where is our revenue actually coming from?',
            'Build an MRR insight from warehouse data',
        ],
    },
    {
        name: 'Forecast burn vs runway',
        stage: 'Startup',
        tags: ['Founder', 'Finance'],
        description: 'Builds a runway chart using a finance data source plus event-driven revenue projections',
        tools_used: 'Data Warehouse, SQL',
        flow: 'external-data-sources-list, execute-sql, insight-create',
        example_prompts: [
            'Build a runway chart from our finance source and revenue trend',
            'How many months of runway do we have at current burn?',
            'Project burn vs revenue for the next year',
        ],
    },
    {
        name: 'Track virality coefficient',
        stage: 'Scale',
        tags: ['Growth', 'Marketing', 'Founder'],
        description: 'Measures invites sent per active user and accepted-invite conversion',
        tools_used: 'Product Analytics, Cohorts',
        flow: 'query-trends, cohorts-create, insight-create',
        example_prompts: [
            'What’s our K-factor — invites sent per user times accept rate?',
            'Track virality week over week',
            'Are invites actually driving new signups?',
        ],
    },
    {
        name: 'Compare your product against a competitor',
        stage: 'Scale',
        tags: ['Marketing', 'Founder', 'Product Management'],
        description: 'Uses surveys and qualitative interviews to compile a side-by-side comparison with a competitor',
        tools_used: 'Surveys, User Interviews, Notebooks',
        flow: 'survey-create, user-interview-topics-create, user-interview-topics-send-invites, notebooks-create',
        example_prompts: [
            'Survey switchers about us vs the competitor and theme the responses',
            'Set up interviews with users who evaluated both and summarize',
            'Build a side-by-side comparison notebook from real user feedback',
        ],
    },
    {
        name: 'Plan a user interview round',
        stage: 'Startup',
        tags: ['Product Management', 'Design'],
        description: 'Defines an interview topic, recruits from a target cohort, and generates booking links',
        tools_used: 'User Interviews, Cohorts',
        flow: 'cohorts-create, user-interview-topics-create, user-interview-topics-interviewees-bulk-create, user-interview-topics-generate-links',
        example_prompts: [
            'Recruit 10 power users for interviews about the new editor and send booking links',
            'Set up an interview round targeting recently churned users',
            'Plan interviews and generate scheduling links for this cohort',
        ],
    },
    {
        name: "Summarize last week's interviews",
        stage: 'Scale',
        tags: ['Product Management', 'Design'],
        description: 'Pulls completed interviews and creates a themed summary notebook',
        tools_used: 'User Interviews, Notebooks',
        flow: 'user-interviews-list, user-interviews-retrieve, notebooks-create, notebook-edit',
        example_prompts: [
            'Summarize last week’s user interviews into themes',
            'What did we hear repeatedly in interviews this sprint?',
            'Turn our completed interviews into a research notebook',
        ],
    },
    {
        name: 'Build a NPS dashboard',
        stage: 'Scale',
        tags: ['Product Management', 'Founder', 'Support'],
        description: 'Runs a recurring NPS survey and visualizes scores by plan and tenure',
        tools_used: 'Surveys, Dashboards',
        flow: 'survey-create, survey-stats, dashboard-create, insight-create',
        example_prompts: [
            'Launch a recurring NPS survey and chart scores by plan',
            'Build an NPS dashboard split by tenure',
            'How has NPS trended since we shipped v2?',
        ],
    },
    {
        name: 'Stand up an early-access feature',
        stage: 'Startup',
        tags: ['Product Management', 'Engineering', 'Marketing'],
        description: 'Creates an early-access feature page and ties it to a beta flag',
        tools_used: 'Early Access Features, Feature Flags',
        flow: 'early-access-feature-create, create-feature-flag, early-access-feature-partial-update',
        example_prompts: [
            'Stand up an early-access program for the new dashboard and wire its beta flag',
            'Let users opt into the beta and gate it behind a flag',
            'Create an early-access feature with a waitlist',
        ],
    },
    {
        name: 'Promote beta users to GA',
        stage: 'Scale',
        tags: ['Product Management', 'Engineering'],
        description: 'Migrates users from a beta cohort to GA and retires the early-access program',
        tools_used: 'Early Access Features, Cohorts, Feature Flags',
        flow: 'early-access-feature-retrieve, cohorts-list, update-feature-flag, early-access-feature-destroy',
        example_prompts: [
            'Roll the beta cohort to GA and retire the early-access program',
            'Ship this feature to everyone and clean up the beta flag',
            'Graduate beta users and close out the program',
        ],
    },
    {
        name: 'Investigate symbol upload failures',
        stage: 'Enterprise',
        tags: ['Engineering', 'Ops'],
        description: 'Lists symbol sets and downloads failing ones to inspect mapping issues',
        tools_used: 'Error Tracking',
        flow: 'error-tracking-symbol-sets-list, error-tracking-symbol-sets-retrieve, error-tracking-symbol-sets-download-retrieve',
        example_prompts: [
            'Why are our stack traces unsymbolicated? Check the symbol sets',
            'Find failing symbol uploads and tell me what’s wrong with the mapping',
            'Inspect the source map that didn’t apply',
        ],
    },
    {
        name: 'Suppress noisy error groups',
        stage: 'Scale',
        tags: ['Engineering', 'Ops'],
        description: 'Adds suppression rules for known-third-party noise so signal-to-noise improves',
        tools_used: 'Error Tracking',
        flow: 'error-tracking-suppression-rules-list, error-tracking-suppression-rules-create, error-tracking-suppression-rules-update',
        example_prompts: [
            'Suppress browser-extension noise so real errors stand out',
            'Add suppression rules for these known third-party errors',
            'Clean up our error feed by muting the noise',
        ],
    },
    {
        name: 'Assign errors to the right team',
        stage: 'Enterprise',
        tags: ['Engineering', 'Ops'],
        description: 'Configures assignment rules so issues land in the correct team’s queue',
        tools_used: 'Error Tracking',
        flow: 'error-tracking-assignment-rules-list, error-tracking-assignment-rules-create, error-tracking-issues-partial-update',
        example_prompts: [
            'Route checkout errors to the payments team automatically',
            'Set up assignment rules so issues land in the right queue',
            'Assign this issue to the team that owns billing',
        ],
    },
    {
        name: 'Merge duplicate error issues',
        stage: 'Scale',
        tags: ['Engineering'],
        description: 'Detects fingerprints that should be a single issue and merges them',
        tools_used: 'Error Tracking',
        flow: 'query-error-tracking-issues-list, error-tracking-issues-merge-create, error-tracking-grouping-rules-create',
        example_prompts: [
            'These three issues are the same bug — merge them',
            'Find duplicate error fingerprints and consolidate',
            'Add a grouping rule so these stop splitting',
        ],
    },
    {
        name: 'Search docs for an API answer',
        stage: 'MVP',
        tags: ['Engineering', 'Support'],
        description: 'Looks up PostHog docs to answer a developer question without leaving the workflow',
        tools_used: 'Docs',
        flow: 'docs-search',
        example_prompts: [
            'How do I capture an event server-side in Python? Check the docs',
            'What’s the API for updating a feature flag?',
            'Find the docs on identifying users and summarize',
        ],
    },
    {
        name: 'Find similar bugs across the org',
        stage: 'Enterprise',
        tags: ['Support', 'Engineering'],
        description: 'Searches entities and conversations to spot tickets that share a root cause',
        tools_used: 'Search, Support',
        flow: 'entity-search, conversations-tickets-list, query-error-tracking-issues-list',
        example_prompts: [
            'Find other tickets that share a root cause with this one',
            'Are these scattered reports actually the same bug?',
            'Search across the org for anything related to this error',
        ],
    },
    {
        name: 'Audit reverse proxy health',
        stage: 'Enterprise',
        tags: ['Engineering', 'Ops'],
        description: 'Lists proxies, diagnoses misconfigurations, and retries failures',
        tools_used: 'Reverse Proxy',
        flow: 'proxy-list, proxy-diagnose, proxy-retry',
        example_prompts: [
            'Are any of our reverse proxies misconfigured? Diagnose and retry',
            'Check proxy health across customer domains',
            'Why is this customer’s proxy failing?',
        ],
    },
    {
        name: 'Add a new reverse proxy',
        stage: 'Enterprise',
        tags: ['Engineering', 'Ops'],
        description: 'Provisions a reverse proxy for a new customer domain',
        tools_used: 'Reverse Proxy',
        flow: 'proxy-create, proxy-diagnose, proxy-get',
        example_prompts: [
            'Provision a reverse proxy for events.acme.com and confirm it’s healthy',
            'Set up a managed proxy for this customer domain',
            'Add a proxy and run diagnostics',
        ],
    },
    {
        name: 'Set up a Replay Vision scanner',
        stage: 'Scale',
        tags: ['Product Management', 'Design', 'Support'],
        description: 'Creates a scanner to surface visual UX issues across recent replays',
        tools_used: 'Replay Vision, Session Replay',
        flow: 'vision-scanners-create, vision-scanners-scan-session, vision-scanners-observations-list',
        example_prompts: [
            'Scan recent replays for visual UX issues like overlap and broken layouts',
            'Set up a vision scanner for the checkout flow',
            'What visual problems is Replay Vision finding this week?',
        ],
    },
    {
        name: 'Surface UX regressions from replays',
        stage: 'Scale',
        tags: ['Design', 'Product Management'],
        description: 'Reviews scanner observations to find new UX problems after a release',
        tools_used: 'Replay Vision',
        flow: 'vision-scanners-list, vision-scanners-observations-list, vision-scanners-observations-get',
        example_prompts: [
            'Did the last release introduce any visual regressions?',
            'Show new UX problems the scanner flagged since Tuesday',
            'Walk me through the worst observation and where it happens',
        ],
    },
    {
        name: 'Cluster user pain points',
        stage: 'Scale',
        tags: ['Product Management', 'Design', 'Support'],
        description: 'Groups support tickets and survey responses into themes for the next sprint',
        tools_used: 'Surveys, Support, Notebooks',
        flow: 'conversations-tickets-list, surveys-global-stats, notebooks-create',
        example_prompts: [
            'Cluster tickets and survey responses into themes for sprint planning',
            'What are users most frustrated about this month?',
            'Turn our feedback into a prioritized pain-point notebook',
        ],
    },
    {
        name: 'Build a custom funnel for ecommerce',
        stage: 'Scale',
        tags: ['Marketing', 'Growth'],
        description: 'Builds an add-to-cart through purchase funnel with breakdowns by source',
        tools_used: 'Product Analytics',
        flow: 'query-funnel, insight-create, dashboard-create',
        example_prompts: [
            'Build an add-to-cart → purchase funnel broken down by traffic source',
            'Which channel converts best from cart to checkout?',
            'Pin our ecommerce funnel to a dashboard',
        ],
    },
    {
        name: 'Run a UTM source breakdown',
        stage: 'Startup',
        tags: ['Marketing', 'Growth'],
        description: 'Breaks down signups by UTM source and identifies highest-LTV channels',
        tools_used: 'Web Analytics, Product Analytics',
        flow: 'query-trends, query-funnel, insight-create',
        example_prompts: [
            'Break signups down by UTM source and tell me the best channel',
            'Which campaigns drive users that actually convert?',
            'Show LTV by acquisition channel',
        ],
    },
    {
        name: 'Score SEO landing pages',
        stage: 'Scale',
        tags: ['Marketing', 'Growth'],
        description: 'Ranks landing pages by entrances, conversion, and bounce to prioritize SEO investment',
        tools_used: 'Web Analytics',
        flow: 'web-analytics-weekly-digest, query-trends, insight-create',
        example_prompts: [
            'Rank our landing pages by entrances, conversion, and bounce',
            'Where should we invest SEO effort next?',
            'Which blog pages convert visitors into signups?',
        ],
    },
    {
        name: 'Identify the next experiment to run',
        stage: 'Scale',
        tags: ['Growth', 'Product Management'],
        description: 'Recommends the highest-leverage page or flow to test based on volume and current conversion',
        tools_used: 'Experiments, Product Analytics',
        flow: 'experiment-list, query-funnel, experiment-create',
        example_prompts: [
            'Where would an experiment move the needle most given our traffic and conversion?',
            'Suggest the next high-leverage test to run',
            'Find the page worth A/B testing and scaffold the experiment',
        ],
    },
    {
        name: 'Stop a losing experiment',
        stage: 'Scale',
        tags: ['Growth', 'Engineering'],
        description: 'Reviews running experiments, identifies losers, and ends or ships variants',
        tools_used: 'Experiments',
        flow: 'experiment-list, experiment-results-get, experiment-end, experiment-ship-variant',
        example_prompts: [
            'Which running experiments are clearly losing? End them',
            'Ship the winner and stop the rest',
            'Review all experiments and clean up the dead ones',
        ],
    },
    {
        name: 'Duplicate a winning experiment',
        stage: 'Scale',
        tags: ['Growth', 'Engineering'],
        description: 'Clones a winning experiment to apply to a sibling surface',
        tools_used: 'Experiments',
        flow: 'experiment-get, experiment-duplicate, experiment-launch',
        example_prompts: [
            'That pricing test won — clone it for the mobile flow and launch',
            'Duplicate this experiment onto the signup page',
            'Reapply the winning hero test to the docs landing page',
        ],
    },
    {
        name: 'Find users similar to a happy account',
        stage: 'Scale',
        tags: ['Sales', 'Growth'],
        description: 'Identifies behavior patterns of a successful customer and finds similar prospects',
        tools_used: 'Cohorts, Persons, Group Analytics',
        flow: 'accounts-retrieve, persons-list, cohorts-create',
        example_prompts: [
            'Profile our happiest account and find prospects that look like them',
            'Build a lookalike cohort from this successful customer',
            'Who behaves like our best account?',
        ],
    },
    {
        name: 'Track recently signed-up companies',
        stage: 'Scale',
        tags: ['Sales', 'Marketing'],
        description: 'Lists new accounts from this week with engagement scores for sales outreach',
        tools_used: 'Group Analytics',
        flow: 'accounts-list, accounts-retrieve, accounts-notebooks-create',
        example_prompts: [
            'List companies that signed up this week with an engagement score',
            'Who should sales reach out to from this week’s signups?',
            'Build account notebooks for our newest companies',
        ],
    },
    {
        name: 'Maintain account notebooks',
        stage: 'Enterprise',
        tags: ['Sales', 'Ops'],
        description: 'Curates ongoing account notebooks for the customer success team',
        tools_used: 'Group Analytics, Notebooks',
        flow: 'accounts-list, accounts-notebooks-list, accounts-notebooks-create, accounts-notebooks-retrieve',
        example_prompts: [
            'Keep our top accounts’ notebooks up to date for CS',
            'Refresh the account notebook for Acme before the QBR',
            'Which accounts are missing a notebook?',
        ],
    },
    {
        name: 'Track product usage metrics per account',
        stage: 'Enterprise',
        tags: ['Sales', 'Founder'],
        description: 'Records and maintains usage metrics on group records for CRM sync',
        tools_used: 'Customer Analytics',
        flow: 'usage-metrics-list, usage-metrics-create, usage-metrics-partial-update',
        example_prompts: [
            'Define seats-active and events-per-week usage metrics on accounts for CRM sync',
            'Maintain per-account usage metrics for Salesforce',
            'Update the usage metric definitions we push to the CRM',
        ],
    },
    {
        name: 'Build an LLM evaluation',
        stage: 'Scale',
        tags: ['Engineering', 'Product Management'],
        description: 'Creates an evaluation harness for an LLM feature with judge models and reports',
        tools_used: 'LLM Observability',
        flow: 'llma-evaluation-create, llma-evaluation-judge-models, llma-evaluation-run, llma-evaluation-report-generate',
        example_prompts: [
            'Set up an eval harness for our support assistant with a judge model',
            'Build an evaluation for answer relevance and run it',
            'Create an eval and generate the first report',
        ],
    },
    {
        name: 'Triage LLM trace reviews',
        stage: 'Scale',
        tags: ['Engineering', 'Product Management'],
        description: 'Walks the LLM review queue and dispositions traces',
        tools_used: 'LLM Observability',
        flow: 'llma-review-queue-list, llma-review-queue-item-list, llma-trace-review-create',
        example_prompts: [
            'Walk the LLM review queue and disposition each trace',
            'What’s waiting in the trace review queue?',
            'Review flagged traces and mark the bad ones',
        ],
    },
    {
        name: 'Configure LLM tagger',
        stage: 'Scale',
        tags: ['Engineering', 'Data'],
        description: 'Sets up a tagger to label LLM traces with intents for downstream analysis',
        tools_used: 'LLM Observability',
        flow: 'llma-tagger-list, llma-tagger-create, llma-tagger-test-hog',
        example_prompts: [
            'Set up a tagger that labels traces by user intent',
            'Auto-tag LLM traces so I can analyze them by topic',
            'Create and test a tagger for refund-related conversations',
        ],
    },
    {
        name: 'Define an LLM score',
        stage: 'Scale',
        tags: ['Engineering', 'Product Management'],
        description: 'Creates a custom score definition (e.g. helpfulness) for evaluating LLM outputs',
        tools_used: 'LLM Observability',
        flow: 'llma-score-definition-list, llma-score-definition-create, llma-score-definition-new-version',
        example_prompts: [
            'Define a helpfulness score for our assistant’s answers',
            'Create a custom score for tone and version it',
            'Add a faithfulness score definition',
        ],
    },
    {
        name: 'Author and version LLM prompts',
        stage: 'Scale',
        tags: ['Engineering', 'Product Management'],
        description: 'Manages a library of versioned prompts and rolls forward winning variants',
        tools_used: 'LLM Observability',
        flow: 'llma-prompt-list, llma-prompt-create, llma-prompt-update, llma-prompt-duplicate',
        example_prompts: [
            'Create a new version of the system prompt and roll it forward',
            'Manage our prompt library and promote the winning variant',
            'Duplicate this prompt and tweak it for the new model',
        ],
    },
    {
        name: 'Build an LLM skill',
        stage: 'Scale',
        tags: ['Engineering', 'Product Management'],
        description: 'Bundles prompts, tools, and files into a reusable LLM skill',
        tools_used: 'LLM Observability',
        flow: 'llma-skill-create, llma-skill-file-create, llma-skill-update, llma-skill-archive',
        example_prompts: [
            'Bundle this workflow into a reusable skill the whole team can call',
            'Create a skill with these prompts and reference files',
            'Turn our refund-handling playbook into a shared skill',
        ],
    },
    {
        name: 'Investigate a single trace tree',
        stage: 'Scale',
        tags: ['Engineering'],
        description: 'Inspects spans for a single trace to find error or latency sources',
        tools_used: 'APM/Tracing',
        flow: 'apm-trace-get, apm-spans-tree, apm-spans-aggregate',
        example_prompts: [
            'Inspect this trace and find where the error or latency starts',
            'Walk the spans for this request id',
            'What’s the slowest span in this trace?',
        ],
    },
    {
        name: 'Build an APM dashboard',
        stage: 'Enterprise',
        tags: ['Engineering', 'Ops'],
        description: 'Builds a dashboard for service-level latency, error rate, and throughput',
        tools_used: 'APM/Tracing, Dashboards',
        flow: 'apm-services-list, apm-spans-aggregate, dashboard-create, insight-create',
        example_prompts: [
            'Build an APM dashboard with latency, error rate, and throughput per service',
            'Give me a service health board for on-call',
            'Chart p95 and error rate for our top services',
        ],
    },
    {
        name: 'Detect a noisy neighbor service',
        stage: 'Enterprise',
        tags: ['Engineering', 'Ops'],
        description: 'Identifies a service that has dramatically increased CPU/IO via APM data',
        tools_used: 'APM/Tracing',
        flow: 'apm-services-list, apm-attribute-values-list, apm-spans-aggregate',
        example_prompts: [
            'Which service spiked its resource usage and is hurting neighbors?',
            'Find the noisy neighbor behind this slowdown',
            'What changed in service load this week?',
        ],
    },
    {
        name: 'Compare deploy impact across regions',
        stage: 'Enterprise',
        tags: ['Engineering', 'Ops'],
        description: 'Looks at error and latency deltas by region after a global deploy',
        tools_used: 'APM/Tracing, Annotations',
        flow: 'annotations-list, apm-spans-aggregate, query-trends',
        example_prompts: [
            'Did the global deploy hit some regions harder? Compare error and latency deltas',
            'Show post-deploy impact by region',
            'Which region regressed most after the release?',
        ],
    },
    {
        name: 'Investigate a logs spike',
        stage: 'Scale',
        tags: ['Engineering', 'Ops'],
        description: 'Determines which service or attribute is driving a logs volume spike',
        tools_used: 'Logs',
        flow: 'logs-count-ranges, logs-sparkline-query, logs-services-create, query-logs',
        example_prompts: [
            'Our log volume just spiked — which service or attribute is driving it?',
            'Find what’s flooding the logs right now',
            'Break down the logs spike by service',
        ],
    },
    {
        name: 'Build a logs dashboard',
        stage: 'Enterprise',
        tags: ['Engineering', 'Ops'],
        description: 'Creates a dashboard combining error logs, latency, and deploy markers',
        tools_used: 'Logs, Dashboards',
        flow: 'query-logs, logs-count, dashboard-create, insight-create',
        example_prompts: [
            'Build a logs dashboard with error rate, latency, and deploy markers',
            'Give me one view of logs health for on-call',
            'Chart error-log volume against our deploys',
        ],
    },
    {
        name: 'Bulk import users into a static cohort',
        stage: 'Enterprise',
        tags: ['Marketing', 'Ops'],
        description: 'Adds a CSV of users to a static cohort for a campaign or experiment audience',
        tools_used: 'Cohorts',
        flow: 'cohorts-create, cohorts-add-persons-to-static-cohort-partial-update, cohorts-retrieve',
        example_prompts: [
            'Import this CSV of emails into a static cohort for the campaign',
            'Build a static audience from our webinar attendee list',
            'Load these users into a cohort and confirm the count',
        ],
    },
    {
        name: 'Remove unsubscribers from cohorts',
        stage: 'Scale',
        tags: ['Marketing', 'Ops'],
        description: 'Removes opted-out users from active marketing cohorts',
        tools_used: 'Cohorts',
        flow: 'cohorts-list, cohorts-rm-person-from-static-cohort-partial-update',
        example_prompts: [
            'Remove anyone who unsubscribed from our active marketing cohorts',
            'Scrub opted-out users before the next send',
            'Take these people out of every campaign audience',
        ],
    },
    {
        name: 'Send a survey to a specific cohort',
        stage: 'Scale',
        tags: ['Product Management', 'Marketing'],
        description: 'Targets an in-app survey to a cohort and tracks responses',
        tools_used: 'Surveys, Cohorts',
        flow: 'cohorts-create, survey-create, survey-stats',
        example_prompts: [
            'Survey our power users about the new editor and report responses',
            'Target this cohort with an in-app survey',
            'Ask enterprise admins one question and track the answers',
        ],
    },
    {
        name: 'Decommission an old survey',
        stage: 'Scale',
        tags: ['Product Management', 'Ops'],
        description: 'Lists active surveys, identifies stale ones, and retires them cleanly',
        tools_used: 'Surveys',
        flow: 'surveys-get-all, survey-get, survey-delete',
        example_prompts: [
            'Which surveys are stale? Retire the ones nobody’s answering',
            'Clean up old surveys that are still running',
            'Archive the launch survey now that it’s done',
        ],
    },
    {
        name: 'Run an end-of-quarter retention review',
        stage: 'Enterprise',
        tags: ['Founder', 'Product Management'],
        description: 'Builds a quarterly retention curve broken down by cohort acquired',
        tools_used: 'Product Analytics, Dashboards',
        flow: 'query-retention, insight-create, dashboard-create',
        example_prompts: [
            'Build a quarterly retention curve split by acquisition cohort',
            'How did each quarter’s signups retain?',
            'Prep the retention review for the board',
        ],
    },
    {
        name: 'Investigate a failed export',
        stage: 'Enterprise',
        tags: ['Data', 'Ops'],
        description: 'Looks up a failing batch export and decides whether to update or recreate',
        tools_used: 'Data Pipelines',
        flow: 'batch-exports-list, batch-export-get, batch-export-update',
        example_prompts: [
            'Our S3 export keeps failing — diagnose and fix the config',
            'Why did this batch export break, and should I recreate it?',
            'Check the failing export and update its settings',
        ],
    },
    {
        name: 'Replay a failed batch export',
        stage: 'Enterprise',
        tags: ['Data', 'Ops'],
        description: 'Cancels a stuck batch export run and re-triggers from the last good checkpoint',
        tools_used: 'Data Pipelines',
        flow: 'batch-exports-list, file-download-batch-exports-cancel-create, file-download-batch-exports-create',
        example_prompts: [
            'This export is stuck — cancel it and re-run from the last checkpoint',
            'Replay yesterday’s failed export window',
            'Restart the warehouse export safely',
        ],
    },
    {
        name: 'Create a new batch export',
        stage: 'Enterprise',
        tags: ['Data', 'Engineering'],
        description: 'Provisions a new batch export to a cloud destination for downstream analytics',
        tools_used: 'Data Pipelines',
        flow: 'batch-export-create, batch-export-update, batch-export-get',
        example_prompts: [
            'Set up a daily batch export of events to BigQuery',
            'Provision an export to our Snowflake instance',
            'Create a new export to S3 and confirm the first run',
        ],
    },
    {
        name: 'Materialize a view to speed analytics',
        stage: 'Enterprise',
        tags: ['Data', 'Engineering'],
        description: 'Materializes a hot view used by multiple dashboards to reduce query time',
        tools_used: 'Data Warehouse',
        flow: 'view-list, view-get, view-materialize, view-run-history',
        example_prompts: [
            'This view backs five dashboards and it’s slow — materialize it',
            'Speed up our heaviest view by materializing it',
            'Check the run history after materializing',
        ],
    },
    {
        name: 'Create a saved SQL variable',
        stage: 'Scale',
        tags: ['Data', 'Engineering'],
        description: 'Creates a SQL variable reused across insights for date or filter selection',
        tools_used: 'Data Warehouse, SQL',
        flow: 'sql-variables-create, sql-variables-update, execute-sql',
        example_prompts: [
            'Create a reusable date-range variable for our SQL insights',
            'Add a plan filter variable I can reuse across queries',
            'Make a saved variable so analysts don’t hardcode dates',
        ],
    },
    {
        name: 'Onboard a new data source',
        stage: 'Enterprise',
        tags: ['Data', 'Engineering'],
        description: 'Connects a new external data source (Stripe, Hubspot) and verifies schemas',
        tools_used: 'Data Warehouse',
        flow: 'external-data-sources-create, external-data-sources-db-schema, external-data-sources-wizard, external-data-schemas-list',
        example_prompts: [
            'Connect Stripe and verify the tables landed correctly',
            'Onboard our HubSpot data and check the schema',
            'Set up a new warehouse source end to end',
        ],
    },
    {
        name: 'Investigate data warehouse health',
        stage: 'Enterprise',
        tags: ['Data', 'Ops'],
        description: 'Surfaces health issues across the warehouse to triage data quality problems',
        tools_used: 'Data Warehouse',
        flow: 'data-warehouse-data-health-issues-retrieve, external-data-sources-list, external-data-sources-jobs',
        example_prompts: [
            'Surface any data-quality issues across the warehouse',
            'Triage warehouse health before the finance close',
            'What’s broken or stale in our data sources?',
        ],
    },
    {
        name: 'Audit who can access a project',
        stage: 'Enterprise',
        tags: ['Security', 'Ops'],
        description: 'Lists members, roles, and recent access changes for a project',
        tools_used: 'Org & Project Management, Activity log',
        flow: 'org-members-list, roles-list, role-members-list, advanced-activity-logs-list',
        example_prompts: [
            'Who can access the production project, and what changed recently?',
            'List members and roles for this project for the access review',
            'Audit access to our sensitive project',
        ],
    },
    {
        name: 'Switch organizations safely',
        stage: 'Enterprise',
        tags: ['Ops'],
        description: 'Switches between orgs and projects programmatically for multi-tenant work',
        tools_used: 'Org & Project Management',
        flow: 'organizations-list, switch-organization, projects-get, switch-project',
        example_prompts: [
            'Switch me into the EU org and its prod project',
            'List my orgs and move to the agency client’s workspace',
            'Change active project to staging',
        ],
    },
    {
        name: 'Create or update a custom role',
        stage: 'Enterprise',
        tags: ['Security', 'Ops'],
        description: 'Defines a role for a customer team and assigns members',
        tools_used: 'Org & Project Management',
        flow: 'roles-list, role-get, role-members-list',
        example_prompts: [
            'Create an “analyst” role with read-only insight access and add the data team',
            'Set up a role for our contractors and assign them',
            'Review who’s in the admin role and trim it',
        ],
    },
    {
        name: 'Add a new alert destination',
        stage: 'Scale',
        tags: ['Engineering', 'Ops'],
        description: 'Configures a new Slack or email destination for logs alerts',
        tools_used: 'Logs, Alerts, Integrations',
        flow: 'integrations-list, logs-alerts-destinations-create, alert-create',
        example_prompts: [
            'Add a Slack destination for our logs alerts',
            'Route critical alerts to the on-call email list',
            'Wire up a new alert channel and test it',
        ],
    },
    {
        name: 'Inspect an integration channel',
        stage: 'Enterprise',
        tags: ['Ops', 'Engineering'],
        description: 'Looks up the channels available on an integration to wire up a workflow',
        tools_used: 'Integrations',
        flow: 'integrations-list, integration-get, integrations-channels-retrieve',
        example_prompts: [
            'Which Slack channels can we post to with our integration?',
            'List available channels before I build this workflow',
            'Inspect our Slack integration’s setup',
        ],
    },
    {
        name: 'Inspect a paywall page',
        stage: 'Scale',
        tags: ['Billing', 'Growth', 'Marketing'],
        description: 'Looks at view-to-upgrade conversion of the paywall page and pulls replays of bouncers',
        tools_used: 'Web Analytics, Session Replay, Product Analytics',
        flow: 'query-trends, query-funnel, query-session-recordings-list',
        example_prompts: [
            'What’s our paywall view-to-upgrade rate, and why do people bounce?',
            'Pull replays of users who hit the paywall and left',
            'Is the paywall converting or just blocking?',
        ],
    },
    {
        name: 'Detect dunning failures',
        stage: 'Scale',
        tags: ['Billing', 'Finance'],
        description: 'Identifies users with multiple failed payments and prepares a recovery workflow',
        tools_used: 'Cohorts, CDP',
        flow: 'cohorts-create, cdp-functions-create',
        example_prompts: [
            'Find users with repeated failed payments and set up a recovery flow',
            'Build a dunning cohort and wire the retry email',
            'Who’s at risk of involuntary churn from card failures?',
        ],
    },
    {
        name: 'Find customers at usage limits',
        stage: 'Scale',
        tags: ['Billing', 'Sales'],
        description: 'Identifies customers approaching plan limits to trigger upgrade conversations',
        tools_used: 'Group Analytics, Cohorts',
        flow: 'accounts-list, cohorts-create, query-trends',
        example_prompts: [
            'Which accounts are about to hit their plan limit?',
            'Build a cohort of customers nearing their cap for upgrade outreach',
            'Who needs a bigger plan based on usage?',
        ],
    },
    {
        name: 'Track price-page experiment',
        stage: 'Scale',
        tags: ['Billing', 'Growth', 'Marketing'],
        description: 'Launches a pricing-page A/B test and analyzes signup conversion impact',
        tools_used: 'Experiments, Web Analytics',
        flow: 'experiment-create, experiment-launch, experiment-results-get, experiment-stats',
        example_prompts: [
            'Launch a pricing-page A/B test and watch signup conversion',
            'Did the new pricing layout hurt conversion? Pull results',
            'Is the pricing experiment significant yet?',
        ],
    },
    {
        name: 'Investigate a Stripe webhook gap',
        stage: 'Scale',
        tags: ['Billing', 'Engineering'],
        description: 'Checks webhook configuration for a billing data source and looks for missed events',
        tools_used: 'Data Warehouse, CDP',
        flow: 'external-data-sources-webhook-info-retrieve, external-data-sources-update-webhook-inputs-create, external-data-sources-reload',
        example_prompts: [
            'Some Stripe events are missing — check the webhook config and reload',
            'Why didn’t these invoices sync from Stripe?',
            'Diagnose the gap in our Stripe webhook and backfill',
        ],
    },
    {
        name: 'Spin up a billing dashboard',
        stage: 'Startup',
        tags: ['Billing', 'Founder', 'Finance'],
        description: 'Builds a starter dashboard with MRR, churn, ARPU, and net new customers',
        tools_used: 'Data Warehouse, SQL, Dashboards',
        flow: 'execute-sql, insight-create, dashboard-create',
        example_prompts: [
            'Build a billing dashboard: MRR, churn, ARPU, net new customers',
            'Give me the four revenue numbers I should watch',
            'Make a starter finance dashboard from Stripe data',
        ],
    },
    {
        name: 'Find the cause of a refund spike',
        stage: 'Scale',
        tags: ['Billing', 'Support', 'Finance'],
        description: 'Correlates refunds with product errors, recent deploys, and customer cohorts',
        tools_used: 'Product Analytics, Error Tracking, Annotations',
        flow: 'query-trends, query-error-tracking-issues-list, annotations-list',
        example_prompts: [
            'Refunds spiked this week — is it tied to an error or a deploy?',
            'Correlate refunds with product issues and recent releases',
            'What’s driving the refund increase?',
        ],
    },
    {
        name: 'Catch a card-decline trend',
        stage: 'Scale',
        tags: ['Billing', 'Finance'],
        description: 'Sets an alert on the daily card-decline rate by country',
        tools_used: 'Alerts, Product Analytics',
        flow: 'query-trends, alert-create',
        example_prompts: [
            'Alert me if the card-decline rate climbs in any country',
            'Track daily declines by region and warn on spikes',
            'Is our decline rate creeping up?',
        ],
    },
    {
        name: 'Detect a suspicious admin action',
        stage: 'Enterprise',
        tags: ['Security', 'Ops'],
        description: 'Searches activity logs for high-risk admin actions and confirms intent with the actor',
        tools_used: 'Activity log',
        flow: 'advanced-activity-logs-filters, advanced-activity-logs-list, activity-log-list',
        example_prompts: [
            'Did anyone export user data or change roles in the last 24h?',
            'Surface high-risk admin actions for review',
            'Who disabled SSO, and when?',
        ],
    },
    {
        name: 'Investigate unusual API token usage',
        stage: 'Enterprise',
        tags: ['Security', 'Engineering'],
        description: 'Looks at recent personal API token activity for unusual scopes or volume',
        tools_used: 'Activity log',
        flow: 'advanced-activity-logs-list, user-get',
        example_prompts: [
            'Is any API token behaving unusually — odd scopes or volume?',
            'Investigate this token’s recent activity',
            'Flag tokens making far more calls than normal',
        ],
    },
    {
        name: 'Lock down access to a sensitive project',
        stage: 'Enterprise',
        tags: ['Security', 'Ops'],
        description: 'Reviews members of a sensitive project and removes non-essential access',
        tools_used: 'Org & Project Management',
        flow: 'org-members-list, role-members-list, project-settings-update',
        example_prompts: [
            'Trim access to the production project to only essential people',
            'Who shouldn’t have access here? Remove them',
            'Lock down this sensitive project',
        ],
    },
    {
        name: 'Verify SOC 2 logging coverage',
        stage: 'Enterprise',
        tags: ['Security'],
        description: 'Confirms that critical actions emit activity log entries for compliance',
        tools_used: 'Activity log',
        flow: 'advanced-activity-logs-filters, advanced-activity-logs-list',
        example_prompts: [
            'Confirm role changes and exports all emit activity-log entries',
            'Verify our audit logging covers the SOC 2 control list',
            'Are all critical actions logged?',
        ],
    },
    {
        name: 'Set up a security alert',
        stage: 'Enterprise',
        tags: ['Security', 'Ops'],
        description: 'Creates an alert for sensitive events like role changes or token rotations',
        tools_used: 'Logs, Alerts',
        flow: 'logs-alerts-create, alert-create',
        example_prompts: [
            'Alert security whenever an admin role is granted',
            'Notify us on token rotations and SSO changes',
            'Set up an alert for sensitive config changes',
        ],
    },
    {
        name: 'Catalog all custom event sources',
        stage: 'Enterprise',
        tags: ['Data', 'Security'],
        description: 'Lists data sources to confirm coverage of expected inputs',
        tools_used: 'Data Warehouse',
        flow: 'external-data-sources-list, external-data-schemas-list, read-data-warehouse-schema',
        example_prompts: [
            'Catalog every data source and confirm we have the inputs we expect',
            'What’s feeding our warehouse right now?',
            'List sources and schemas for the data inventory',
        ],
    },
    {
        name: 'Find prototype event firehose',
        stage: 'MVP',
        tags: ['Engineering', 'Data'],
        description: 'Reviews the live event feed during early build to confirm events fire correctly',
        tools_used: 'Events, Properties',
        flow: 'event-definitions-list, properties-list, query-run',
        example_prompts: [
            'Show me the live event feed so I can confirm things are firing',
            'Are my new events arriving with the right properties?',
            'Tail recent events while I test the integration',
        ],
    },
    {
        name: 'Find the first 100 active users',
        stage: 'MVP',
        tags: ['Founder', 'Product Management'],
        description: 'Lists the first 100 users to perform a critical action so the team can interview them',
        tools_used: 'Persons, Product Analytics',
        flow: 'persons-list, query-run',
        example_prompts: [
            'List our first 100 users who completed the core action',
            'Who were our earliest activated users? I want to talk to them',
            'Pull the first hundred people who hit the aha moment',
        ],
    },
    {
        name: 'Quick activation rate sanity check',
        stage: 'MVP',
        tags: ['Founder', 'Product Management'],
        description: 'Computes signup-to-activation rate for the last 7 days',
        tools_used: 'Product Analytics',
        flow: 'query-funnel, query-run',
        example_prompts: [
            'What’s our signup-to-activation rate over the last 7 days?',
            'Quick check: how many new users actually activate?',
            'Is activation up or down this week?',
        ],
    },
    {
        name: 'Daily active users sanity check',
        stage: 'MVP',
        tags: ['Founder', 'Product Management'],
        description: 'Builds and pins a DAU/WAU/MAU chart for the new product',
        tools_used: 'Product Analytics, Dashboards',
        flow: 'query-trends, insight-create, dashboard-create',
        example_prompts: [
            'Build and pin a DAU/WAU/MAU chart',
            'How many people use the product each day?',
            'Give me the active-users chart on my home',
        ],
    },
    {
        name: 'Validate a critical event fires',
        stage: 'MVP',
        tags: ['Engineering', 'Data'],
        description: 'Confirms a brand-new event arrives end-to-end and has the right properties',
        tools_used: 'Events, Properties, SDK Doctor',
        flow: 'sdk-doctor-get, event-definitions-list, properties-list, query-run',
        example_prompts: [
            'Confirm the “subscription_started” event arrives with the right properties',
            'Did my new checkout event make it end to end?',
            'Validate this critical event before launch',
        ],
    },
    {
        name: 'Create a first feature flag',
        stage: 'MVP',
        tags: ['Engineering', 'Product Management'],
        description: 'Creates a hello-world feature flag with environments and rollout rules',
        tools_used: 'Feature Flags',
        flow: 'create-feature-flag, feature-flag-get-definition, update-feature-flag',
        example_prompts: [
            'Create my first feature flag with a 10% rollout',
            'Set up a flag for the new nav and gate it to the team',
            'Make a hello-world flag and confirm its config',
        ],
    },
    {
        name: 'Compare blast radius before a flag rollout',
        stage: 'Scale',
        tags: ['Engineering', 'Product Management'],
        description: 'Evaluates how many users a flag change would affect before pushing it',
        tools_used: 'Feature Flags',
        flow: 'feature-flag-get-definition, feature-flags-user-blast-radius-create',
        example_prompts: [
            'How many users would this flag change actually affect?',
            'Check the blast radius before I ramp this flag',
            'Who gets hit if I flip this to 50%?',
        ],
    },
    {
        name: 'Trace why a flag returned a value',
        stage: 'Scale',
        tags: ['Engineering', 'Support'],
        description: 'Determines which rule caused a flag evaluation for a debugging support request',
        tools_used: 'Feature Flags',
        flow: 'feature-flag-get-definition, feature-flags-evaluation-reasons-retrieve, feature-flags-test-evaluation-create',
        example_prompts: [
            'Why did this user get the beta variant? Show which rule matched',
            'Debug why the flag returned false for this account',
            'Trace this flag evaluation for the support ticket',
        ],
    },
    {
        name: 'Find a user by email',
        stage: 'MVP',
        tags: ['Support', 'Engineering'],
        description: 'Looks up a person, inspects their properties, and lists their recent sessions',
        tools_used: 'Persons, Session Replay',
        flow: 'persons-list, persons-retrieve, query-session-recordings-list',
        example_prompts: [
            'Look up tom@acme.com and show their properties and recent sessions',
            'Find this user and pull their last few replays',
            'What do we know about this person?',
        ],
    },
    {
        name: "Update a person's property",
        stage: 'Scale',
        tags: ['Engineering', 'Ops'],
        description: 'Updates a property on a person record during a data hygiene cleanup',
        tools_used: 'Persons',
        flow: 'persons-retrieve, persons-property-set, persons-property-delete',
        example_prompts: [
            'Fix the plan property on this person — it’s stale',
            'Set is_internal=true on our teammates’ profiles',
            'Remove the deprecated property from this user',
        ],
    },
    {
        name: 'Bulk delete test users',
        stage: 'Enterprise',
        tags: ['Data', 'Ops'],
        description: 'Removes test or QA persons from production before launch',
        tools_used: 'Persons',
        flow: 'persons-list, persons-bulk-delete',
        example_prompts: [
            'Delete all QA and test accounts from production',
            'Clean out internal test users before launch',
            'Remove persons with @test.com emails',
        ],
    },
    {
        name: 'Generate a one-page PMF report',
        stage: 'Startup',
        tags: ['Founder', 'Product Management'],
        description: 'Combines NPS, retention, and qualitative responses into a single notebook',
        tools_used: 'Surveys, Product Analytics, Notebooks',
        flow: 'survey-stats, query-retention, notebooks-create, notebook-edit',
        example_prompts: [
            'Pull NPS, retention, and survey quotes into a one-page PMF report',
            'Summarize where we stand on product-market fit',
            'Build the PMF notebook for the investor update',
        ],
    },
    {
        name: 'Compare two product versions',
        stage: 'Scale',
        tags: ['Product Management', 'Engineering'],
        description: 'Compares engagement before vs after a major redesign using annotations as boundaries',
        tools_used: 'Annotations, Product Analytics',
        flow: 'annotations-list, query-trends, query-retention',
        example_prompts: [
            'Did engagement improve after the redesign? Compare before vs after the annotation',
            'Compare retention pre- and post-v2',
            'Was the rewrite worth it by the numbers?',
        ],
    },
    {
        name: 'Reset a misconfigured experiment',
        stage: 'Scale',
        tags: ['Engineering', 'Product Management'],
        description: 'Resets results on an experiment that was misconfigured at launch',
        tools_used: 'Experiments',
        flow: 'experiment-get, experiment-reset, experiment-update, experiment-resume',
        example_prompts: [
            'This experiment launched with the wrong metric — reset and fix it',
            'Clear the bad results and restart this test cleanly',
            'Reconfigure and resume the experiment',
        ],
    },
    {
        name: 'Archive completed experiments',
        stage: 'Enterprise',
        tags: ['Product Management', 'Ops'],
        description: 'Archives experiments older than 90 days that have shipped or stopped',
        tools_used: 'Experiments',
        flow: 'experiment-list, experiment-archive, experiment-unarchive',
        example_prompts: [
            'Archive every experiment older than 90 days that’s already shipped or stopped',
            'Clean up our experiments list',
            'Tidy old experiments but keep the ones still running',
        ],
    },
    {
        name: 'Build a saved experiment metric library',
        stage: 'Enterprise',
        tags: ['Product Management', 'Data'],
        description: 'Defines a shared library of saved metrics so every experiment uses consistent definitions',
        tools_used: 'Experiments',
        flow: 'experiment-saved-metrics-list, experiment-saved-metrics-create, experiment-saved-metrics-partial-update',
        example_prompts: [
            'Create shared saved metrics so every experiment measures activation the same way',
            'Standardize our experiment metrics into a library',
            'Add a saved revenue metric all tests can reuse',
        ],
    },
    {
        name: 'Inspect experiment time-series results',
        stage: 'Scale',
        tags: ['Product Management', 'Data'],
        description: "Pulls daily experiment results to spot novelty effects and Simpson's-paradox issues",
        tools_used: 'Experiments',
        flow: 'experiment-timeseries-results, experiment-results-get, experiment-stats',
        example_prompts: [
            'Show daily results for this experiment — is the lift a novelty effect?',
            'Check for Simpson’s paradox in this test',
            'Plot the experiment over time, not just the summary',
        ],
    },
    {
        name: 'Diagnose a flaky test ingestion',
        stage: 'Scale',
        tags: ['Engineering', 'Data'],
        description: 'Investigates why test environments are leaking into prod analytics',
        tools_used: 'Events, Cohorts, Persons',
        flow: 'event-definitions-list, persons-list, cohorts-create, persons-bulk-delete',
        example_prompts: [
            'Test events are polluting prod — find the source and clean it up',
            'Why is staging traffic showing in production analytics?',
            'Build a cohort of leaked test users and delete them',
        ],
    },
    {
        name: 'Set up an LLM evaluation report',
        stage: 'Scale',
        tags: ['Engineering', 'Product Management'],
        description: 'Schedules a recurring evaluation report to track regressions over time',
        tools_used: 'LLM Observability',
        flow: 'llma-evaluation-report-create, llma-evaluation-report-generate, llma-evaluation-report-run-list',
        example_prompts: [
            'Schedule a weekly eval report so we catch quality regressions',
            'Track our assistant’s eval scores over time',
            'Set up a recurring evaluation report and generate the first run',
        ],
    },
    {
        name: 'Compare LLM judge models',
        stage: 'Scale',
        tags: ['Engineering'],
        description: 'Compares scores from different judge models on the same evaluation',
        tools_used: 'LLM Observability',
        flow: 'llma-evaluation-judge-models, llma-evaluation-run, llma-evaluation-report-generate',
        example_prompts: [
            'Compare how different judge models score the same eval set',
            'Which judge model agrees most with our human reviews?',
            'Run this eval across two judges and diff the scores',
        ],
    },
    {
        name: 'Find users that produced bad LLM responses',
        stage: 'Scale',
        tags: ['Support', 'Engineering'],
        description: 'Filters LLM traces with low scores to find affected users',
        tools_used: 'LLM Observability, Persons',
        flow: 'query-llm-traces-list, query-llm-trace, persons-retrieve',
        example_prompts: [
            'Which users got low-quality answers from the assistant this week?',
            'Find traces scored badly and tell me who was affected',
            'Pull the people we should proactively follow up with',
        ],
    },
    {
        name: "Build a 'wow moment' dashboard",
        stage: 'Startup',
        tags: ['Founder', 'Product Management', 'Growth'],
        description: 'Tracks the share of users who hit the activation moment within their first session',
        tools_used: 'Product Analytics, Dashboards',
        flow: 'query-funnel, query-trends, dashboard-create',
        example_prompts: [
            'What share of new users hit the aha moment in their first session?',
            'Build a wow-moment dashboard for activation',
            'Track first-session activation over time',
        ],
    },
    {
        name: 'Spin up a competitor landing page test',
        stage: 'Scale',
        tags: ['Marketing', 'Growth'],
        description: "Builds a 'vs competitor' landing page and tests it against the default homepage",
        tools_used: 'Experiments, Web Analytics',
        flow: 'experiment-create, experiment-launch, experiment-results-get',
        example_prompts: [
            'Test a “vs Competitor” landing page against our homepage',
            'Does the comparison page convert better for branded search?',
            'Launch and measure a competitor-alternative page',
        ],
    },
    {
        name: 'Track founder-mode key metrics weekly',
        stage: 'Startup',
        tags: ['Founder'],
        description: 'Pins a Friday-afternoon dashboard with the 5 metrics the founder watches',
        tools_used: 'Dashboards, Product Analytics',
        flow: 'insight-create, dashboard-create, query-trends, query-retention',
        example_prompts: [
            'Pin a dashboard with the five numbers I check every Friday',
            'Build my founder metrics view: signups, activation, retention, revenue, NPS',
            'Set up my weekly snapshot',
        ],
    },
    {
        name: 'Tag every active product in the org',
        stage: 'Enterprise',
        tags: ['Ops', 'Product Management'],
        description: 'Tags entities by product line for analytics rollups across a large org',
        tools_used: 'Feature Flags, Insights, Dashboards',
        flow: 'feature-flag-get-all, feature-flags-bulk-update-tags-create, insights-list',
        example_prompts: [
            'Tag every flag, insight, and dashboard by product line',
            'Set up product-line tags so we can roll metrics up',
            'Bulk-tag entities for the new analytics governance model',
        ],
    },
    {
        name: 'Investigate a customer report of stale data',
        stage: 'Enterprise',
        tags: ['Support', 'Data'],
        description: 'Checks data warehouse sync status and view refresh history to confirm freshness',
        tools_used: 'Data Warehouse',
        flow: 'external-data-sources-jobs, external-data-sync-logs, view-run-history',
        example_prompts: [
            'A customer says their numbers are stale — check sync and view refresh status',
            'When did this view last refresh, and did the sync succeed?',
            'Confirm data freshness for this account',
        ],
    },
    {
        name: 'Run a SQL exploration',
        stage: 'Scale',
        tags: ['Data'],
        description: 'Runs ad-hoc SQL with variables to explore a hypothesis',
        tools_used: 'SQL',
        flow: 'hogql-schema, sql-variables-create, execute-sql',
        example_prompts: [
            'Explore whether weekend signups retain worse with some ad-hoc SQL',
            'Run a quick query with a date variable to test this hunch',
            'Help me poke at this hypothesis in SQL',
        ],
    },
    {
        name: 'Build an executive North-Star widget',
        stage: 'Enterprise',
        tags: ['Founder', 'Product Management'],
        description: "Builds a single insight tracking the company's north-star metric with goal lines",
        tools_used: 'Insights, Dashboards',
        flow: 'insight-create, insight-update, dashboard-create',
        example_prompts: [
            'Build a north-star insight with goal lines for the year',
            'Make one big-number widget for our key metric and add a target',
            'Track weekly active teams against our goal',
        ],
    },
    {
        name: 'Surface trending insights',
        stage: 'Scale',
        tags: ['Product Management'],
        description: 'Lists insights with the largest week-over-week movement for an exec briefing',
        tools_used: 'Insights',
        flow: 'insights-trending-retrieve, insights-list, insight-get',
        example_prompts: [
            'Which insights moved the most week over week?',
            'Show me the biggest movers for the exec briefing',
            'What changed sharply in our metrics this week?',
        ],
    },
    {
        name: 'Audit who edited an insight',
        stage: 'Enterprise',
        tags: ['Ops', 'Data'],
        description: 'Reviews who recently edited a critical insight for governance',
        tools_used: 'Insights, Activity log',
        flow: 'insight-get, insights-activity-retrieve, insights-all-activity-retrieve',
        example_prompts: [
            'Who changed the revenue insight, and what did they change?',
            'Audit recent edits to this critical insight',
            'Show the edit history before we trust this number',
        ],
    },
    {
        name: 'Discover and tag stale insights',
        stage: 'Enterprise',
        tags: ['Ops', 'Data'],
        description: 'Finds insights not opened in 6+ months and proposes archival',
        tools_used: 'Insights',
        flow: 'insights-list, insights-activity-retrieve, insight-update',
        example_prompts: [
            'Find insights nobody’s opened in 6 months and propose archiving them',
            'Tag stale insights for cleanup',
            'Which insights are dead weight?',
        ],
    },
    {
        name: 'Create a delivery subscription',
        stage: 'Scale',
        tags: ['Ops', 'Marketing'],
        description: 'Subscribes stakeholders to a weekly delivery of a dashboard via email or Slack',
        tools_used: 'Subscriptions',
        flow: 'subscriptions-list, subscriptions-create, subscriptions-test-delivery-create',
        example_prompts: [
            'Email the exec team this dashboard every Monday at 8am',
            'Subscribe #growth to a weekly Slack delivery of the funnel',
            'Set up a recurring dashboard delivery and test it',
        ],
    },
    {
        name: 'Audit subscription deliveries',
        stage: 'Enterprise',
        tags: ['Ops'],
        description: 'Confirms that scheduled deliveries are reaching recipients',
        tools_used: 'Subscriptions',
        flow: 'subscriptions-deliveries-list, subscriptions-deliveries-retrieve',
        example_prompts: [
            'Are our scheduled dashboard deliveries actually landing?',
            'Check delivery history for the exec digest',
            'Did last week’s subscription send fail for anyone?',
        ],
    },
    {
        name: 'Comment on a flagged dashboard tile',
        stage: 'Scale',
        tags: ['Product Management', 'Ops'],
        description: 'Adds a comment thread to a dashboard tile to coordinate review',
        tools_used: 'Dashboards, Comments',
        flow: 'comments-list, comment-thread, comment-count',
        example_prompts: [
            'Leave a comment on this tile asking the data team to verify it',
            'Start a thread on the revenue tile for review',
            'Flag this chart with a question for the owner',
        ],
    },
    {
        name: 'Resolve outstanding comments',
        stage: 'Enterprise',
        tags: ['Ops'],
        description: 'Sweeps unresolved comments and decides whether to act, close, or escalate',
        tools_used: 'Comments',
        flow: 'comments-list, comment-get, comment-thread',
        example_prompts: [
            'Sweep open comments and tell me which need action vs can be closed',
            'What unresolved comments are waiting on me?',
            'Triage outstanding comment threads',
        ],
    },
    {
        name: 'Triage open user feedback',
        stage: 'Scale',
        tags: ['Support', 'Product Management'],
        description: 'Reviews recent user feedback submissions, tags themes, and assigns owners',
        tools_used: 'Feedback, Surveys',
        flow: 'agent-feedback, surveys-global-stats, conversations-tickets-list',
        example_prompts: [
            'Review this week’s feedback, theme it, and assign owners',
            'What are users asking for most in feedback?',
            'Tag and route open feedback to the right teams',
        ],
    },
    {
        name: 'Run an in-product poll',
        stage: 'MVP',
        tags: ['Product Management'],
        description: 'Launches a one-question in-product poll to gather sentiment fast',
        tools_used: 'Surveys',
        flow: 'survey-create, survey-stats, survey-update',
        example_prompts: [
            'Launch a one-question poll asking if the new layout is better',
            'Quick in-app poll on which integration to build next',
            'Ask users one question and show me the results',
        ],
    },
    {
        name: 'Update an existing survey targeting',
        stage: 'Scale',
        tags: ['Product Management'],
        description: 'Refines targeting on a running survey to focus on a critical persona',
        tools_used: 'Surveys, Cohorts',
        flow: 'survey-get, cohorts-list, survey-update',
        example_prompts: [
            'Narrow this survey to only enterprise admins',
            'Retarget the running survey to activated users',
            'Tighten survey targeting to the cohort that matters',
        ],
    },
    {
        name: 'Validate an insight before sharing',
        stage: 'Scale',
        tags: ['Data', 'Product Management'],
        description: 'Runs the underlying query and validates HogQL before publishing an insight to leadership',
        tools_used: 'Insights, SQL',
        flow: 'insight-get, query-validate, query-run',
        example_prompts: [
            'Double-check this insight’s query before I send it to leadership',
            'Validate the HogQL behind this number',
            'Is this insight actually measuring what I think?',
        ],
    },
    {
        name: 'Quickly answer a single product question',
        stage: 'MVP',
        tags: ['Founder', 'Product Management'],
        description: 'Converts a Slack-style question into a one-off query for a fast answer',
        tools_used: 'Insights, SQL',
        flow: 'query-generate-hogql-from-question, query-validate, query-run',
        example_prompts: [
            'How many teams created a second project last week?',
            'Answer this quickly: signups from Germany yesterday',
            'Just give me the number — daily actives on iOS',
        ],
    },
    {
        name: 'Investigate a single session',
        stage: 'MVP',
        tags: ['Support', 'Engineering'],
        description: 'Pulls and summarizes a single session by id for a support escalation',
        tools_used: 'Session Replay',
        flow: 'session-recording-get, session-recording-summarize',
        example_prompts: [
            'Summarize what happened in session abc123 for this escalation',
            'Walk me through this user’s session step by step',
            'What went wrong in this recording?',
        ],
    },
    {
        name: 'Save a curated replay playlist',
        stage: 'Scale',
        tags: ['Support', 'Product Management'],
        description: 'Builds a playlist of session recordings for the design team to review',
        tools_used: 'Session Replay',
        flow: 'session-recording-playlists-list, session-recording-playlist-create, session-recording-playlist-update',
        example_prompts: [
            'Build a playlist of onboarding replays for the design review',
            'Save a playlist of sessions that hit the new feature',
            'Curate replays of confused users for the team',
        ],
    },
    {
        name: 'Delete a session at user request',
        stage: 'Enterprise',
        tags: ['Security', 'Support'],
        description: 'Honors a privacy request to delete a session recording',
        tools_used: 'Session Replay',
        flow: 'session-recording-get, session-recording-delete',
        example_prompts: [
            'A user requested deletion — remove this session recording',
            'Honor this GDPR request and delete the replay',
            'Delete the recording tied to this privacy ticket',
        ],
    },
    {
        name: 'Tag a key event as needing definition',
        stage: 'Startup',
        tags: ['Data', 'Product Management'],
        description: 'Annotates an event with description and owner so the catalog stays clean',
        tools_used: 'Events',
        flow: 'event-definitions-list, event-definition-update',
        example_prompts: [
            'Add a description and owner to the “activated” event',
            'Document this event so the catalog stays clean',
            'Mark which events still need definitions',
        ],
    },
    {
        name: 'Audit property usage across queries',
        stage: 'Enterprise',
        tags: ['Data'],
        description: 'Finds rarely-used properties and proposes deprecation',
        tools_used: 'Events, Properties',
        flow: 'properties-list, property-definitions, insights-list',
        example_prompts: [
            'Which event properties are barely used and safe to deprecate?',
            'Audit property usage so we can trim the schema',
            'Find properties no insight references',
        ],
    },
    {
        name: 'Spin up a churn-risk alert',
        stage: 'Scale',
        tags: ['Sales', 'Support'],
        description: "Sets an alert when a high-value account's weekly activity drops below a threshold",
        tools_used: 'Group Analytics, Alerts',
        flow: 'accounts-retrieve, insight-create, alert-create',
        example_prompts: [
            'Alert CS when Acme’s weekly usage drops below threshold',
            'Warn me when any top account goes quiet',
            'Set a churn-risk alert on our biggest customers',
        ],
    },
    {
        name: 'Detect a backend deploy regression',
        stage: 'Scale',
        tags: ['Engineering'],
        description: 'Looks at p95 latency delta against the latest deploy annotation',
        tools_used: 'APM/Tracing, Annotations',
        flow: 'annotations-list, apm-spans-aggregate, query-apm-spans',
        example_prompts: [
            'Did the latest deploy regress p95 latency? Compare against the annotation',
            'Is the API slower since this morning’s release?',
            'Check for a latency regression tied to the deploy',
        ],
    },
    {
        name: "Pin a 'launch day' watchroom dashboard",
        stage: 'Scale',
        tags: ['Engineering', 'Product Management'],
        description: 'Builds a launch-day dashboard with errors, signups, and adoption side-by-side',
        tools_used: 'Dashboards, Error Tracking, Product Analytics',
        flow: 'dashboard-create, insight-create, query-trends, query-error-tracking-issues-list',
        example_prompts: [
            'Build a launch-day watchroom: errors, signups, and adoption side by side',
            'Give us one screen to watch during the launch',
            'Set up the war-room dashboard for tomorrow’s release',
        ],
    },
    {
        name: 'Find session that triggered an error',
        stage: 'Scale',
        tags: ['Support', 'Engineering'],
        description: 'Given an error id, finds the linked session and pulls the replay',
        tools_used: 'Error Tracking, Session Replay',
        flow: 'query-error-tracking-issue, query-error-tracking-issue-events, session-recording-get',
        example_prompts: [
            'Find the session that triggered this error and pull the replay',
            'Show me a recording of someone hitting this exception',
            'Link this error to the user’s session',
        ],
    },
    {
        name: 'Find sessions by URL',
        stage: 'MVP',
        tags: ['Support', 'Product Management'],
        description: 'Lists sessions that visited a specific URL during a time window',
        tools_used: 'Session Replay',
        flow: 'query-session-recordings-list, session-recording-get',
        example_prompts: [
            'Show sessions that visited /pricing yesterday',
            'Pull replays from anyone who hit the new docs page this week',
            'Find recordings for this URL during the campaign',
        ],
    },
    {
        name: 'Audit endpoint usage',
        stage: 'Enterprise',
        tags: ['Engineering', 'Ops'],
        description: 'Lists endpoints and their last execution to identify abandoned ones',
        tools_used: 'Endpoints',
        flow: 'endpoints-get-all, endpoint-get, endpoint-versions',
        example_prompts: [
            'Which data endpoints haven’t been called in months?',
            'Audit endpoint usage and flag abandoned ones',
            'When was this endpoint last run?',
        ],
    },
    {
        name: 'Spin up a public data endpoint',
        stage: 'Scale',
        tags: ['Engineering'],
        description: 'Creates a public endpoint for an internal team to query without dashboard access',
        tools_used: 'Endpoints',
        flow: 'endpoint-create, endpoint-materialization-status, endpoint-openapi-spec, endpoint-run',
        example_prompts: [
            'Expose this query as a public endpoint for the finance team',
            'Create a data endpoint so partners can pull usage without dashboard access',
            'Publish an endpoint and give me its OpenAPI spec',
        ],
    },
    {
        name: 'Add an action to track a UI click',
        stage: 'Startup',
        tags: ['Product Management', 'Engineering'],
        description: 'Defines a no-code action for a click without requiring an SDK release',
        tools_used: 'Actions',
        flow: 'actions-get-all, action-create, action-update',
        example_prompts: [
            'Create a no-code action for clicks on the upgrade button',
            'Track this CTA without shipping new code',
            'Define an action for the new nav link',
        ],
    },
    {
        name: 'Decommission stale actions',
        stage: 'Enterprise',
        tags: ['Ops'],
        description: 'Lists actions that no insight or experiment uses and deletes them',
        tools_used: 'Actions',
        flow: 'actions-get-all, action-get, action-delete',
        example_prompts: [
            'Delete actions that no insight or experiment uses',
            'Clean up our unused actions',
            'Which actions are safe to remove?',
        ],
    },
    {
        name: 'Set up a homepage funnel watchroom',
        stage: 'Scale',
        tags: ['Marketing', 'Growth'],
        description: 'Creates a live homepage-funnel watchroom with alerts on top dropoffs',
        tools_used: 'Product Analytics, Alerts, Dashboards',
        flow: 'query-funnel, insight-create, dashboard-create, alert-create',
        example_prompts: [
            'Build a live homepage funnel and alert me if a step drops sharply',
            'Watch our top-of-funnel conversion with alerts',
            'Set up a funnel watchroom for the campaign launch',
        ],
    },
    {
        name: "Investigate a single account's health",
        stage: 'Enterprise',
        tags: ['Sales', 'Support'],
        description: 'Pulls account properties, recent usage trend, and open conversations for a QBR',
        tools_used: 'Group Analytics, Product Analytics, Support',
        flow: 'accounts-retrieve, query-trends, conversations-tickets-list',
        example_prompts: [
            'Prep a health summary on Acme for the QBR — usage trend and open tickets',
            'How is this account doing overall?',
            'Pull everything I need before the renewal call',
        ],
    },
    {
        name: 'Plan a launch with annotations and flags',
        stage: 'Scale',
        tags: ['Product Management', 'Engineering'],
        description: 'Pre-creates flags, annotations, and dashboards needed for an upcoming launch',
        tools_used: 'Feature Flags, Annotations, Dashboards',
        flow: 'create-feature-flag, annotation-create, dashboard-create',
        example_prompts: [
            'Set up the flag, launch annotation, and dashboard for next week’s release',
            'Prep everything we need to track the launch',
            'Scaffold our launch instrumentation',
        ],
    },
    {
        name: 'Spot drift in a recurring funnel',
        stage: 'Scale',
        tags: ['Product Management', 'Growth'],
        description: "Compares this week's funnel conversion vs the trailing 8-week average",
        tools_used: 'Product Analytics',
        flow: 'query-funnel, insight-create',
        example_prompts: [
            'Is this week’s funnel conversion drifting from the 8-week average?',
            'Compare current onboarding conversion to its trailing baseline',
            'Has our funnel quietly gotten worse?',
        ],
    },
    {
        name: 'Detect a sudden drop in event volume',
        stage: 'Scale',
        tags: ['Engineering', 'Data'],
        description: 'Checks event counts per stream and alerts when any drops below baseline',
        tools_used: 'Alerts, Logs',
        flow: 'logs-count-ranges, query-trends, alert-create',
        example_prompts: [
            'Did any event stream suddenly stop? Alert me if volume drops',
            'A tracking change may have broken ingestion — check for drops',
            'Warn us when an event falls below its baseline',
        ],
    },
    {
        name: 'Catch a regression with an APM-driven alert',
        stage: 'Enterprise',
        tags: ['Engineering', 'Ops'],
        description: 'Alerts when p99 latency on a critical service spikes',
        tools_used: 'APM/Tracing, Alerts',
        flow: 'apm-services-list, apm-spans-aggregate, alert-create',
        example_prompts: [
            'Alert on-call when p99 on the checkout service spikes',
            'Set a latency-regression alert for our critical path',
            'Warn us before users feel the slowdown',
        ],
    },
    {
        name: "Build a 'top 10 issues' notebook",
        stage: 'Scale',
        tags: ['Engineering', 'Product Management'],
        description: 'Snapshots the top error issues this week into a notebook for a triage meeting',
        tools_used: 'Error Tracking, Notebooks',
        flow: 'query-error-tracking-issues-list, notebooks-create, notebook-edit',
        example_prompts: [
            'Snapshot this week’s top 10 errors into a notebook for triage',
            'Prep the bug triage doc from error tracking',
            'Build the weekly issues notebook',
        ],
    },
    {
        name: 'Run end-to-end docs lookups for new SDK',
        stage: 'MVP',
        tags: ['Engineering'],
        description: 'Drives a new SDK install by pulling docs answers on the fly',
        tools_used: 'Docs',
        flow: 'docs-search',
        example_prompts: [
            'Walk me through installing the new SDK, pulling docs as we go',
            'How do I set up session replay in React Native?',
            'Answer my install questions from the docs as I build',
        ],
    },
    {
        name: 'Run an inbox triage',
        stage: 'Scale',
        tags: ['Support', 'Ops'],
        description: 'Walks the support inbox and dispositions reports to the right product area',
        tools_used: 'Signals, Support',
        flow: 'inbox-reports-list, inbox-reports-retrieve, conversations-tickets-update',
        example_prompts: [
            'Triage today’s Signals inbox and route each report to the right area',
            'Disposition the inbox and, for the top crash, have PostHog Desktop open a PR',
            'Clear the inbox and tag everything by product',
        ],
    },
    {
        name: 'Inspect inbox source configs',
        stage: 'Enterprise',
        tags: ['Ops'],
        description: 'Audits which inbound sources feed the inbox and ensures coverage',
        tools_used: 'Signals',
        flow: 'inbox-source-configs-list, inbox-source-configs-retrieve',
        example_prompts: [
            'Which sources feed our Signals inbox? Confirm coverage',
            'Audit inbox source configs for gaps',
            'Are we ingesting from every channel we expect?',
        ],
    },
    {
        name: "Track a feature's revenue impact",
        stage: 'Scale',
        tags: ['Product Management', 'Finance'],
        description: 'Correlates feature adoption with revenue uplift via SQL on warehouse data',
        tools_used: 'Data Warehouse, SQL, Cohorts',
        flow: 'cohorts-create, read-data-warehouse-schema, execute-sql',
        example_prompts: [
            'Do users who adopt templates spend more? Correlate adoption with revenue',
            'Measure the revenue impact of the new feature',
            'Is this feature paying for itself?',
        ],
    },
    {
        name: 'Identify a referral champion',
        stage: 'Scale',
        tags: ['Growth', 'Marketing'],
        description: 'Finds users who drove the most successful referrals this quarter',
        tools_used: 'Product Analytics, Cohorts, Persons',
        flow: 'query-trends, cohorts-create, persons-list',
        example_prompts: [
            'Who drove the most successful referrals this quarter?',
            'Build a cohort of our top referrers to reward them',
            'Find our referral champions',
        ],
    },
    {
        name: 'Track a viral loop',
        stage: 'Scale',
        tags: ['Growth', 'Marketing'],
        description: 'Builds a paths analysis from invite-sent to invitee-activated',
        tools_used: 'Product Analytics',
        flow: 'query-paths, query-funnel',
        example_prompts: [
            'Map the loop from invite sent to invitee activated',
            'Where does our viral loop leak?',
            'Build a paths analysis for referrals',
        ],
    },
    {
        name: 'Stand up a brand-new project',
        stage: 'MVP',
        tags: ['Founder', 'Engineering'],
        description: 'Spins up a fresh PostHog project, captures basic settings, and confirms with a sanity event',
        tools_used: 'Org & Project Management',
        flow: 'organizations-list, switch-organization, projects-get, project-settings-update',
        example_prompts: [
            'Spin up a fresh project for the new app and set sane defaults',
            'Create a project and confirm a test event lands',
            'Set up a new PostHog project for our side project',
        ],
    },
    {
        name: 'Verify a brand-new ingestion pipeline',
        stage: 'MVP',
        tags: ['Engineering'],
        description: 'Validates new ingestion by inspecting data schema and a sample query',
        tools_used: 'Data Schema, Events, SQL',
        flow: 'read-data-schema, event-definitions-list, execute-sql',
        example_prompts: [
            'Confirm our new pipeline is landing events with the right schema',
            'Validate ingestion with a sample query',
            'Is data actually flowing into the new project?',
        ],
    },
    {
        name: "Make a 'first dashboard' for a new product",
        stage: 'MVP',
        tags: ['Founder', 'Product Management'],
        description: 'Builds a starter dashboard for a brand new product with 3 insights',
        tools_used: 'Dashboards, Product Analytics',
        flow: 'insight-create, dashboard-create, dashboard-update',
        example_prompts: [
            'Build my first dashboard with three starter insights',
            'Give me a sensible default dashboard for a new product',
            'Set up signups, actives, and retention on one board',
        ],
    },
    {
        name: 'Hello-world feature flag rollout',
        stage: 'MVP',
        tags: ['Engineering'],
        description: 'Creates a percentage rollout flag to test the end-to-end loop',
        tools_used: 'Feature Flags',
        flow: 'create-feature-flag, update-feature-flag, feature-flag-get-definition',
        example_prompts: [
            'Create a test flag at 50% so I can verify the SDK loop',
            'Make my first rollout flag and bump it to 100%',
            'Set up a hello-world flag end to end',
        ],
    },
    {
        name: 'Hello-world survey launch',
        stage: 'MVP',
        tags: ['Product Management'],
        description: 'Launches a one-question survey to verify SDK survey wiring',
        tools_used: 'Surveys',
        flow: 'survey-create, survey-stats',
        example_prompts: [
            'Launch a one-question survey to confirm surveys are wired up',
            'Test our survey setup with a quick poll',
            'Create my first survey and check it renders',
        ],
    },
    {
        name: 'Hello-world experiment launch',
        stage: 'MVP',
        tags: ['Engineering', 'Growth'],
        description: 'Creates and launches a simple A/B test to validate experimentation wiring',
        tools_used: 'Experiments',
        flow: 'experiment-create, experiment-launch, experiment-results-get',
        example_prompts: [
            'Create a simple A/B test to confirm experiments work end to end',
            'Launch my first experiment and show results',
            'Validate our experimentation setup with a hello-world test',
        ],
    },
    {
        name: 'Hello-world cohort',
        stage: 'MVP',
        tags: ['Product Management'],
        description: 'Creates a small static cohort and confirms membership',
        tools_used: 'Cohorts',
        flow: 'cohorts-create, cohorts-add-persons-to-static-cohort-partial-update, cohorts-retrieve',
        example_prompts: [
            'Create a small static cohort and confirm who’s in it',
            'Make my first cohort with a few test users',
            'Set up a cohort and verify membership',
        ],
    },
    {
        name: 'Hello-world dashboard subscription',
        stage: 'MVP',
        tags: ['Founder'],
        description: 'Subscribes to a daily delivery to validate notification channels',
        tools_used: 'Subscriptions',
        flow: 'subscriptions-create, subscriptions-test-delivery-create',
        example_prompts: [
            'Subscribe me to a daily delivery to test notifications',
            'Send myself a test dashboard email',
            'Confirm our subscription channel works',
        ],
    },
    {
        name: "Set the user's home settings",
        stage: 'MVP',
        tags: ['Ops'],
        description: 'Personalizes the user’s home view to surface critical insights first',
        tools_used: 'Platform Features',
        flow: 'user-home-settings-get, user-home-settings-update',
        example_prompts: [
            'Put my key metrics first on my home view',
            'Personalize my home to surface the funnel and retention',
            'Set up my default landing dashboard',
        ],
    },
    {
        name: 'Verify your own user account',
        stage: 'MVP',
        tags: ['Ops'],
        description: 'Confirms your identity and updates settings for the workspace',
        tools_used: 'Core',
        flow: 'user-get, user-settings-update',
        example_prompts: [
            'Confirm who I’m authenticated as and update my settings',
            'Check my account and set my timezone',
            'Verify my workspace settings',
        ],
    },
    {
        name: 'Quick start the docs guru',
        stage: 'MVP',
        tags: ['Engineering', 'Support'],
        description: 'Fields a steady stream of docs lookups while a teammate ramps on PostHog',
        tools_used: 'Docs',
        flow: 'docs-search',
        example_prompts: [
            'Answer my PostHog questions from the docs as I onboard',
            'How do I create a cohort vs a static list?',
            'Be my docs guru while I learn the product',
        ],
    },
    {
        name: 'Pick a product North Star',
        stage: 'MVP',
        tags: ['Founder', 'Growth'],
        description: 'Picks a North Star metric for an MVP product and builds the supporting insight',
        tools_used: 'Insights',
        flow: 'insight-create, query-trends, dashboard-create',
        example_prompts: [
            'Help me pick a north-star metric for my MVP and build the insight',
            'What single metric should I optimize right now?',
            'Set up my north-star and supporting chart',
        ],
    },
    {
        name: 'Build an onboarding funnel',
        stage: 'MVP',
        tags: ['Growth', 'Product Management'],
        description: 'Builds the first onboarding funnel for an MVP and identifies the worst dropoff',
        tools_used: 'Product Analytics',
        flow: 'query-funnel, insight-create',
        example_prompts: [
            'Build my onboarding funnel and tell me the worst step',
            'Where do new users fall out of signup?',
            'Set up the activation funnel for my MVP',
        ],
    },
    {
        name: 'Review day-one errors',
        stage: 'MVP',
        tags: ['Engineering'],
        description: 'Enables error tracking and lists the day’s top issues',
        tools_used: 'Error Tracking',
        flow: 'query-error-tracking-issues-list, query-error-tracking-issue, error-tracking-grouping-rules-list',
        example_prompts: [
            'Turn on error tracking and show me today’s top issues',
            'What’s breaking in my MVP right now?',
            'List the errors I should fix first',
        ],
    },
    {
        name: 'Session recordings for the founder',
        stage: 'Startup',
        tags: ['Founder', 'Product Management'],
        description: 'Curates a daily playlist of 5 fresh sessions for the founder to watch',
        tools_used: 'Session Replay',
        flow: 'query-session-recordings-list, session-recording-playlist-create',
        example_prompts: [
            'Give me 5 fresh sessions to watch over coffee each morning',
            'Curate a daily founder replay playlist',
            'Show me real users using the product today',
        ],
    },
    {
        name: 'LLM cost guardrails',
        stage: 'MVP',
        tags: ['Engineering', 'Finance'],
        description: 'Adds a spend alert and shows the team’s LLM cost trajectory',
        tools_used: 'LLM Observability, Alerts',
        flow: 'get-llm-total-costs-for-project, alert-create',
        example_prompts: [
            'Show our LLM spend trajectory and alert me if it spikes',
            'Set a budget guardrail on AI costs',
            'How much are we spending on the model this month, and warn me at $2k',
        ],
    },
    {
        name: 'Build a GTM dashboard',
        stage: 'Startup',
        tags: ['Marketing', 'Sales', 'Founder'],
        description: 'Builds a GTM dashboard with signups, conversion, ICP fit, and account growth',
        tools_used: 'Web Analytics, Group Analytics, Dashboards',
        flow: 'query-trends, accounts-list, dashboard-create',
        example_prompts: [
            'Build a GTM dashboard: signups, conversion, ICP fit, account growth',
            'Give sales and marketing one shared board',
            'Track our go-to-market funnel end to end',
        ],
    },
    {
        name: 'Track hiring metrics',
        stage: 'Startup',
        tags: ['Founder', 'Ops'],
        description: 'Tracks engineering output (PRs, deploys, errors) for a growing team',
        tools_used: 'Annotations, Error Tracking',
        flow: 'annotations-list, query-trends, query-error-tracking-issues-list',
        example_prompts: [
            'Track our deploy cadence and error rate as the team grows',
            'Are we shipping faster without breaking more?',
            'Build an engineering-output view',
        ],
    },
    {
        name: 'Build a user interview pipeline',
        stage: 'Startup',
        tags: ['Product Management', 'Founder'],
        description: 'Builds a recurring weekly pipeline of user interviews',
        tools_used: 'User Interviews',
        flow: 'user-interview-topics-create, user-interview-topics-add-interviewee, user-interview-topics-send-invites',
        example_prompts: [
            'Set up a recurring weekly user-interview pipeline',
            'Keep 3 interviews booked every week automatically',
            'Build an always-on interview cadence',
        ],
    },
    {
        name: 'Activation deep dive',
        stage: 'Startup',
        tags: ['Growth', 'Product Management'],
        description: 'Decomposes activation rate by source, plan, and segment',
        tools_used: 'Product Analytics',
        flow: 'query-funnel, query-trends, insight-create',
        example_prompts: [
            'Break activation down by source, plan, and segment',
            'Who activates best, and who doesn’t?',
            'Deep-dive on why activation varies',
        ],
    },
    {
        name: 'Design a referral program',
        stage: 'Startup',
        tags: ['Growth', 'Marketing'],
        description: 'Designs and measures a referral program with a flag and a cohort',
        tools_used: 'Feature Flags, Cohorts, Product Analytics',
        flow: 'create-feature-flag, cohorts-create, query-funnel',
        example_prompts: [
            'Design a referral program behind a flag and measure its funnel',
            'Stand up referrals for a test cohort and track conversions',
            'Build and instrument a referral loop',
        ],
    },
    {
        name: 'Data quality bug bash',
        stage: 'Scale',
        tags: ['Data', 'Engineering'],
        description: 'Runs a one-day bug bash on event taxonomy, fixing names and adding descriptions',
        tools_used: 'Events, Properties',
        flow: 'event-definitions-list, event-definition-update, properties-list',
        example_prompts: [
            'Run a taxonomy bug bash — fix bad event names and add descriptions',
            'Clean up our messiest events and properties',
            'Find inconsistent event naming and propose fixes',
        ],
    },
    {
        name: 'Experiment hygiene sweep',
        stage: 'Scale',
        tags: ['Product Management'],
        description: 'Reviews all experiments, archives stale ones, and ships winners',
        tools_used: 'Experiments',
        flow: 'experiment-list, experiment-archive, experiment-ship-variant, experiment-end',
        example_prompts: [
            'Sweep all experiments — archive stale, ship winners, end losers',
            'Clean up our experiment backlog',
            'Bring order to our running tests',
        ],
    },
    {
        name: 'Dashboards spring cleaning',
        stage: 'Scale',
        tags: ['Ops', 'Product Management'],
        description: 'Audits and consolidates duplicate dashboards across teams',
        tools_used: 'Dashboards',
        flow: 'dashboards-get-all, dashboard-get, dashboard-delete',
        example_prompts: [
            'Find duplicate dashboards across teams and consolidate them',
            'Spring-clean our dashboards',
            'Which dashboards can we safely delete?',
        ],
    },
    {
        name: 'Feature-flag debt cleanup',
        stage: 'Scale',
        tags: ['Engineering', 'Ops'],
        description: 'Identifies fully-rolled-out flags and proposes code cleanup',
        tools_used: 'Feature Flags',
        flow: 'feature-flag-get-all, feature-flags-status-retrieve, feature-flags-bulk-delete-create',
        example_prompts: [
            'Find flags at 100% and open a PR removing them from the code',
            'List flag debt we can clean up',
            'Which flags are permanently on and safe to retire?',
        ],
    },
    {
        name: 'Error tracking grooming',
        stage: 'Scale',
        tags: ['Engineering'],
        description: 'Merges duplicates, assigns owners, suppresses noise across error tracking',
        tools_used: 'Error Tracking',
        flow: 'query-error-tracking-issues-list, error-tracking-issues-merge-create, error-tracking-suppression-rules-create',
        example_prompts: [
            'Groom error tracking: merge dupes, assign owners, suppress noise',
            'Clean up our issue list end to end',
            'Get our error tracking back under control',
        ],
    },
    {
        name: 'SOC 2 evidence pack',
        stage: 'Enterprise',
        tags: ['Security'],
        description: 'Compiles the evidence for SOC 2 controls from activity logs and change requests',
        tools_used: 'Activity log, Approvals',
        flow: 'advanced-activity-logs-list, change-requests-list, approval-policies-list',
        example_prompts: [
            'Compile our SOC 2 evidence pack from activity logs and change requests',
            'Pull the audit evidence for this quarter’s controls',
            'Get me ready for the SOC 2 audit',
        ],
    },
    {
        name: 'Enterprise customer onboarding',
        stage: 'Enterprise',
        tags: ['Sales', 'Ops'],
        description: 'Drives onboarding of a new enterprise customer end-to-end',
        tools_used: 'Org & Project Management, Group Analytics, Dashboards',
        flow: 'organization-get, accounts-create, project-settings-update, dashboard-create',
        example_prompts: [
            'Onboard a new enterprise customer: set up their account, project, and dashboard',
            'Stand up everything Acme needs to get started',
            'Drive enterprise onboarding end to end',
        ],
    },
    {
        name: 'Data residency check',
        stage: 'Enterprise',
        tags: ['Security', 'Data'],
        description: 'Confirms that data sources and exports comply with residency requirements',
        tools_used: 'Data Warehouse, Activity log',
        flow: 'external-data-sources-list, batch-exports-list, advanced-activity-logs-list',
        example_prompts: [
            'Confirm our sources and exports comply with EU data residency',
            'Is any data leaving the region it shouldn’t?',
            'Run a data-residency compliance check',
        ],
    },
    {
        name: 'Change governance review',
        stage: 'Enterprise',
        tags: ['Ops', 'Security'],
        description: 'Reviews change requests, approvals, and rollback paths on a recurring cadence',
        tools_used: 'Activity log, Approvals',
        flow: 'change-requests-list, approval-policies-list, advanced-activity-logs-list',
        example_prompts: [
            'Run this week’s change-governance review with rollback paths',
            'Summarize change requests and their approval status',
            'Prep the governance review packet',
        ],
    },
    {
        name: 'Quarterly access review',
        stage: 'Enterprise',
        tags: ['Security', 'Ops'],
        description: 'Lists members, roles, and access changes for the quarterly review',
        tools_used: 'Org & Project Management, Activity log',
        flow: 'org-members-list, roles-list, role-members-list, advanced-activity-logs-list',
        example_prompts: [
            'Build the quarterly access review: members, roles, and recent changes',
            'Who has access to what, and what changed this quarter?',
            'Prep the access recertification',
        ],
    },
    {
        name: 'Find an underserved persona',
        stage: 'Startup',
        tags: ['Founder', 'Product Management'],
        description: "Combines surveys and analytics to surface a persona competitors don't focus on",
        tools_used: 'Surveys, Cohorts',
        flow: 'survey-create, survey-stats, cohorts-create',
        example_prompts: [
            'Find a persona that loves us but competitors ignore',
            'Survey users to surface an underserved segment',
            'Which user type should we double down on?',
        ],
    },
    {
        name: 'Shock-test pricing',
        stage: 'Scale',
        tags: ['Growth', 'Billing'],
        description: 'Runs a bold price experiment with a subset of new visitors',
        tools_used: 'Experiments',
        flow: 'experiment-create, experiment-launch, experiment-results-get',
        example_prompts: [
            'Test doubling our price on a slice of new visitors',
            'Run a bold pricing experiment and watch conversion',
            'Can we charge more? Shock-test it',
        ],
    },
    {
        name: "Kill a feature that's holding you back",
        stage: 'Scale',
        tags: ['Product Management'],
        description: 'Identifies a feature with low usage and high maintenance cost and proposes deprecation',
        tools_used: 'Product Analytics, Feature Flags',
        flow: 'query-trends, feature-flag-get-all, update-feature-flag',
        example_prompts: [
            'Which feature has low usage but high maintenance cost? Propose killing it',
            'Find the feature we should sunset and flag it off',
            'What can we cut without anyone noticing?',
        ],
    },
    {
        name: 'Weekly competitor radar',
        stage: 'Scale',
        tags: ['Marketing', 'Founder'],
        description: 'Schedules a weekly competitor scan and writes findings into a shared notebook',
        tools_used: 'Notebooks, Annotations',
        flow: 'notebooks-create, notebook-edit, annotation-create',
        example_prompts: [
            'Run a weekly competitor scan and log findings to our radar notebook',
            'Keep a running competitor watch and annotate big moves',
            'Set up our weekly competitive radar',
        ],
    },
    {
        name: "'Why are we losing deals' analysis",
        stage: 'Scale',
        tags: ['Sales', 'Founder'],
        description: 'Pulls lost-deal interviews and themes to inform positioning',
        tools_used: 'User Interviews, Notebooks',
        flow: 'user-interviews-search, user-interviews-retrieve, notebooks-create',
        example_prompts: [
            'Theme our lost-deal interviews — why are we losing?',
            'What objections come up most in deals we lost?',
            'Summarize lost-deal feedback for positioning',
        ],
    },
    {
        name: 'Alternative onboarding experiment',
        stage: 'Scale',
        tags: ['Growth', 'Product Management'],
        description: 'Tests a totally different onboarding path against the standard funnel',
        tools_used: 'Experiments, Product Analytics',
        flow: 'experiment-create, experiment-launch, query-funnel, experiment-results-get',
        example_prompts: [
            'Test a radically different onboarding against the current funnel',
            'Try a “skip setup” path and measure activation',
            'Experiment with an alternative first-run experience',
        ],
    },
    {
        name: 'Compare org-wide insights at a glance',
        stage: 'Enterprise',
        tags: ['Founder', 'Product Management'],
        description: 'Lists all insights across every project with recency and owner',
        tools_used: 'Insights',
        flow: 'insights-list, insights-all-activity-retrieve, insight-get',
        example_prompts: [
            'List every insight across all projects with owner and last update',
            'Give me an org-wide inventory of insights',
            'Which projects have the most active analytics?',
        ],
    },
    {
        name: 'Discover annotations across teams',
        stage: 'Enterprise',
        tags: ['Ops'],
        description: 'Lists annotations to map releases, incidents, and experiments over time',
        tools_used: 'Annotations',
        flow: 'annotations-list, annotation-retrieve, annotations-partial-update',
        example_prompts: [
            'Map all annotations into a timeline of releases and incidents',
            'What happened across teams over the last quarter?',
            'Build a timeline from our annotations',
        ],
    },
    {
        name: 'Maintain a customer-facing endpoint',
        stage: 'Enterprise',
        tags: ['Engineering'],
        description: 'Updates a public endpoint after a schema change and verifies materialization',
        tools_used: 'Endpoints',
        flow: 'endpoint-get, endpoint-update, endpoint-materialization-status, endpoint-run',
        example_prompts: [
            'The schema changed — update the public endpoint and verify materialization',
            'Keep our customer-facing endpoint in sync after the migration',
            'Update and re-run the partner endpoint',
        ],
    },
    {
        name: 'Bootstrap a self-serve docs bot',
        stage: 'Scale',
        tags: ['Engineering', 'Support'],
        description: 'Wires up a docs-search loop that answers support questions before a human sees them',
        tools_used: 'Docs, Support',
        flow: 'docs-search, conversations-tickets-list, conversations-tickets-update',
        example_prompts: [
            'Answer incoming support questions from the docs before a human sees them',
            'Deflect common tickets with a docs-search loop',
            'Auto-respond to “how do I” tickets using the docs',
        ],
    },
    {
        name: 'Run a finance close on warehouse data',
        stage: 'Enterprise',
        tags: ['Finance', 'Data'],
        description: 'Runs the end-of-month reconciliation queries against the data warehouse',
        tools_used: 'Data Warehouse, SQL',
        flow: 'read-data-warehouse-schema, sql-variables-create, execute-sql',
        example_prompts: [
            'Run our month-end reconciliation queries against the warehouse',
            'Close the books — pull the standard finance queries',
            'Reconcile revenue for last month',
        ],
    },
    {
        name: 'Build a unit-economics dashboard',
        stage: 'Enterprise',
        tags: ['Finance', 'Founder'],
        description: 'Combines CAC, payback, and LTV into a single unit economics dashboard',
        tools_used: 'Data Warehouse, Dashboards, Insights',
        flow: 'execute-sql, insight-create, dashboard-create',
        example_prompts: [
            'Build a unit-economics dashboard: CAC, payback, LTV',
            'Show our LTV:CAC by segment',
            'Do our unit economics actually work?',
        ],
    },
    {
        name: 'Investigate a revenue dip',
        stage: 'Enterprise',
        tags: ['Finance'],
        description: 'Investigates a daily revenue dip via warehouse SQL and dunning cohorts',
        tools_used: 'Data Warehouse, Cohorts',
        flow: 'execute-sql, cohorts-create, query-trends',
        example_prompts: [
            'Revenue dipped yesterday — is it churn, declines, or a data gap?',
            'Investigate the daily revenue drop',
            'What’s behind this week’s revenue softness?',
        ],
    },
    {
        name: 'Build an invoice aging report',
        stage: 'Enterprise',
        tags: ['Finance'],
        description: 'Builds an invoice aging report from warehouse data and pings owners',
        tools_used: 'Data Warehouse, SQL',
        flow: 'external-data-sources-list, execute-sql, insight-create',
        example_prompts: [
            'Build an invoice aging report and flag anything 60+ days overdue',
            'Who owes us money and for how long?',
            'Pull AR aging from the warehouse',
        ],
    },
    {
        name: 'Forecast renewals',
        stage: 'Enterprise',
        tags: ['Finance', 'Sales'],
        description: 'Projects this-quarter renewals based on usage trend and conversation sentiment',
        tools_used: 'Group Analytics, Support, SQL',
        flow: 'accounts-list, conversations-tickets-list, execute-sql',
        example_prompts: [
            'Forecast this quarter’s renewals from usage trend and ticket sentiment',
            'Which renewals are at risk?',
            'Project renewal revenue and flag shaky accounts',
        ],
    },
    {
        name: 'Audit usage-based billing',
        stage: 'Enterprise',
        tags: ['Finance', 'Billing'],
        description: 'Validates usage counts that drive customer invoices',
        tools_used: 'Data Warehouse, SQL, Customer Analytics',
        flow: 'usage-metrics-list, execute-sql, view-run',
        example_prompts: [
            'Validate the usage counts behind this month’s invoices',
            'Audit usage-based billing for discrepancies',
            'Do our metered charges match actual usage?',
        ],
    },
    {
        name: 'Weekly traffic breakdown',
        stage: 'Scale',
        tags: ['Marketing'],
        description: 'Builds a weekly breakdown of traffic by source, region, and converting page',
        tools_used: 'Web Analytics',
        flow: 'web-analytics-weekly-digest, query-trends, insight-create',
        example_prompts: [
            'Break down this week’s traffic by source, region, and converting page',
            'Where did our visitors come from last week?',
            'Build the weekly web traffic report',
        ],
    },
    {
        name: 'Measure blog post performance',
        stage: 'Scale',
        tags: ['Marketing'],
        description: 'Measures blog post views, scroll depth, and conversion to signup',
        tools_used: 'Web Analytics, Product Analytics',
        flow: 'query-trends, query-funnel, insight-create',
        example_prompts: [
            'Which blog posts drive the most signups?',
            'Measure views, scroll depth, and conversion for our posts',
            'Did last week’s launch post move the needle?',
        ],
    },
    {
        name: 'Review email-driven activations',
        stage: 'Scale',
        tags: ['Marketing', 'Growth'],
        description: 'Tracks email-driven users from open through activation',
        tools_used: 'CDP, Product Analytics',
        flow: 'cdp-functions-list, query-funnel, query-trends',
        example_prompts: [
            'Track email-driven users from open through activation',
            'Are our lifecycle emails actually activating users?',
            'Measure the funnel from email click to aha moment',
        ],
    },
    {
        name: 'Identify product-qualified leads',
        stage: 'Scale',
        tags: ['Sales'],
        description: 'Builds a PQL cohort using usage thresholds and account properties',
        tools_used: 'Cohorts, Group Analytics',
        flow: 'cohorts-create, accounts-list, query-trends',
        example_prompts: [
            'Build a PQL cohort from usage thresholds and account size',
            'Who are our product-qualified leads this week?',
            'Surface accounts ready for a sales touch',
        ],
    },
    {
        name: 'Route a stalled trial',
        stage: 'Scale',
        tags: ['Sales'],
        description: 'Identifies trials that stalled and routes them to an AE with session recordings',
        tools_used: 'Cohorts, Session Replay, Notebooks',
        flow: 'cohorts-create, query-session-recordings-list, accounts-notebooks-create',
        example_prompts: [
            'Find trials that stalled and hand them to an AE with replays attached',
            'Which trials went quiet mid-setup? Route them to sales',
            'Build account notebooks for stalled trials',
        ],
    },
    {
        name: 'Ship a new component safely',
        stage: 'Scale',
        tags: ['Design', 'Engineering'],
        description: 'Wraps a new UI component in a flag, sets adoption metrics, and ships incrementally',
        tools_used: 'Feature Flags, Experiments, Session Replay',
        flow: 'create-feature-flag, experiment-create, query-session-recordings-list',
        example_prompts: [
            'Wrap the new component in a flag and measure adoption as we ramp',
            'Ship this redesign incrementally and watch session recordings',
            'Roll out the new nav safely with a flag and an experiment',
        ],
    },
    {
        name: 'Heatmap-driven redesign',
        stage: 'Scale',
        tags: ['Design'],
        description: 'Uses heatmaps and session recordings to scope a redesign of a high-friction page',
        tools_used: 'Heatmaps, Session Replay',
        flow: 'query-session-recordings-list, session-recording-summarize',
        example_prompts: [
            'Use heatmaps and session recordings to scope a redesign of /settings',
            'Where do users click and get stuck on this page?',
            'Gather evidence for the redesign from real sessions',
        ],
    },
    {
        name: 'Validate copy with a survey',
        stage: 'Scale',
        tags: ['Design', 'Marketing'],
        description: 'Polls users on a copy variant before shipping it broadly',
        tools_used: 'Surveys',
        flow: 'survey-create, survey-stats',
        example_prompts: [
            'Poll users on two headline options before we ship',
            'Which CTA copy resonates more? Run a quick survey',
            'Validate this messaging with a survey first',
        ],
    },
    {
        name: 'Reach out to top customers about a feature',
        stage: 'Scale',
        tags: ['Sales', 'Product Management'],
        description: 'Builds a list of high-usage customers for a beta invite',
        tools_used: 'Group Analytics, Cohorts',
        flow: 'accounts-list, cohorts-create, accounts-notebooks-create',
        example_prompts: [
            'List our heaviest users of this feature to invite to the beta',
            'Who should we recruit for early access?',
            'Build a beta-invite list from top accounts',
        ],
    },
    {
        name: "Customer 'wow moment' postmortem",
        stage: 'Scale',
        tags: ['Product Management', 'Growth'],
        description: 'Identifies sessions where a user clearly had a wow moment and extracts patterns',
        tools_used: 'Session Replay, Product Analytics',
        flow: 'query-session-recordings-list, session-recording-summarize, query-funnel',
        example_prompts: [
            'Find sessions where users clearly got value and tell me the common path',
            'What leads to a wow moment? Extract the pattern',
            'Reverse-engineer our best activations from session recordings',
        ],
    },
    {
        name: 'Cluster bug reports into themes',
        stage: 'Enterprise',
        tags: ['Support', 'Engineering'],
        description: 'Themes recent tickets to surface recurring underlying bugs',
        tools_used: 'Support, Error Tracking',
        flow: 'conversations-tickets-list, query-error-tracking-issues-list, notebooks-create',
        example_prompts: [
            'Theme recent tickets to surface the recurring underlying bugs',
            'Which bugs are generating the most support volume?',
            'Group bug reports and link them to error issues',
        ],
    },
    {
        name: "Confirm a 'no repro' bug",
        stage: 'Scale',
        tags: ['Engineering', 'Support'],
        description: 'Tries to find a matching session and logs entry for a customer-reported bug',
        tools_used: 'Session Replay, Logs, Error Tracking',
        flow: 'query-session-recordings-list, query-logs, query-error-tracking-issues-list',
        example_prompts: [
            'I can’t reproduce this — find a session and logs that show it happening',
            'Did this bug actually occur for the customer? Prove it from session recordings',
            'Confirm the reported issue with real session evidence',
        ],
    },
    {
        name: 'Investigate a deploy-error correlation',
        stage: 'Scale',
        tags: ['Engineering', 'Ops'],
        description: 'Correlates deploy annotations with error spikes and identifies likely culprits',
        tools_used: 'Annotations, Error Tracking',
        flow: 'annotations-list, query-error-tracking-issues-list, query-error-tracking-issue-events',
        example_prompts: [
            'Tie this error spike to a deploy and name the likely culprit',
            'Which release introduced this issue?',
            'Correlate deploys with errors and open a revert PR if it’s clear',
        ],
    },
    {
        name: "Run a 'cancel survey' for churned users",
        stage: 'Scale',
        tags: ['Product Management', 'Growth'],
        description: 'Triggers a cancel survey at churn and aggregates reasons over time',
        tools_used: 'Surveys, Cohorts',
        flow: 'cohorts-create, survey-create, survey-stats',
        example_prompts: [
            'Trigger a cancel survey at churn and aggregate the reasons',
            'Why are people canceling? Ask them on the way out',
            'Build a churn-reason report from cancel surveys',
        ],
    },
    {
        name: 'Plan a re-engagement campaign',
        stage: 'Scale',
        tags: ['Marketing', 'Growth'],
        description: 'Builds a cohort of dormant users and launches a workflow with conversion tracking',
        tools_used: 'Cohorts, Workflows, Product Analytics',
        flow: 'cohorts-create, workflows-list, query-trends',
        example_prompts: [
            'Build a dormant cohort and launch a re-engagement workflow with conversion tracking',
            'Win back users who’ve gone quiet for 30 days',
            'Set up and measure a reactivation campaign',
        ],
    },
    {
        name: 'Generate a one-pager on a feature',
        stage: 'Scale',
        tags: ['Product Management'],
        description: 'Combines adoption, retention, and survey responses into a feature one-pager',
        tools_used: 'Notebooks, Surveys, Product Analytics',
        flow: 'query-trends, survey-stats, notebooks-create, notebook-edit',
        example_prompts: [
            'Write a one-pager on the new editor: adoption, retention, and feedback',
            'Summarize how this feature is performing for the team',
            'Build the feature scorecard notebook',
        ],
    },
    {
        name: 'Translate exec questions into queries',
        stage: 'Scale',
        tags: ['Founder', 'Data'],
        description: "Converts an exec's free-form question into runnable SQL with validation",
        tools_used: 'SQL, Insights',
        flow: 'query-generate-hogql-from-question, query-validate, execute-sql',
        example_prompts: [
            'The CEO asked “are enterprise accounts growing faster?” — answer it in SQL',
            'Turn this exec question into a validated query and run it',
            'Answer the board’s question with real numbers',
        ],
    },
    {
        name: 'Confirm a fix went out cleanly',
        stage: 'Scale',
        tags: ['Engineering'],
        description: 'Confirms a fix shipped by watching the error volume after a deploy annotation',
        tools_used: 'Error Tracking, Annotations',
        flow: 'annotations-list, query-error-tracking-issues-list',
        example_prompts: [
            'Did our fix actually work? Watch the error volume after the deploy',
            'Confirm this issue stopped happening post-release',
            'Verify the patch landed cleanly',
        ],
    },
    {
        name: "Build a 'risk heatmap' for accounts",
        stage: 'Enterprise',
        tags: ['Sales'],
        description: 'Scores accounts on usage delta, tickets, and contract value to flag risk',
        tools_used: 'Group Analytics, Support, Customer Analytics',
        flow: 'accounts-list, conversations-tickets-list, usage-metrics-list',
        example_prompts: [
            'Build a risk heatmap scoring accounts on usage, tickets, and contract value',
            'Which big accounts are most at risk?',
            'Rank our book of business by churn risk',
        ],
    },
    {
        name: 'Spin up a daily growth standup',
        stage: 'Scale',
        tags: ['Growth'],
        description: 'Builds a tile-by-tile daily standup dashboard for the growth team',
        tools_used: 'Dashboards, Product Analytics',
        flow: 'insight-create, dashboard-create, query-trends',
        example_prompts: [
            'Build a daily growth standup dashboard',
            'What should growth look at every morning?',
            'Set up our daily metrics board',
        ],
    },
    {
        name: 'Spin up a daily engineering standup',
        stage: 'Scale',
        tags: ['Engineering'],
        description: 'Builds a dashboard with deploys, error rates, p95 latency, and on-call alerts',
        tools_used: 'Dashboards, APM/Tracing, Error Tracking, Annotations',
        flow: 'annotations-list, apm-spans-aggregate, query-error-tracking-issues-list, dashboard-create',
        example_prompts: [
            'Build an engineering standup board: deploys, errors, p95, alerts',
            'What does the team need to see each morning?',
            'Set up our on-call health dashboard',
        ],
    },
    {
        name: 'Daily ops standup digest',
        stage: 'Scale',
        tags: ['Ops'],
        description: 'Sends a daily ops digest with sync status, alerts triggered, and access changes',
        tools_used: 'Subscriptions, Activity log',
        flow: 'external-data-sources-jobs, alerts-list, advanced-activity-logs-list, subscriptions-create',
        example_prompts: [
            'Send a daily ops digest: sync status, alerts triggered, access changes',
            'What broke or changed overnight?',
            'Set up our morning ops summary',
        ],
    },
    {
        name: "Inspect a single feature flag's dependents",
        stage: 'Enterprise',
        tags: ['Engineering'],
        description: 'Lists insights and experiments that depend on a flag before changes',
        tools_used: 'Feature Flags',
        flow: 'feature-flag-get-definition, feature-flags-dependent-flags-retrieve, feature-flags-status-retrieve',
        example_prompts: [
            'What depends on this flag before I change it?',
            'List insights and experiments tied to this flag',
            'Is it safe to delete this flag?',
        ],
    },
    {
        name: 'Copy flags between projects',
        stage: 'Enterprise',
        tags: ['Engineering', 'Ops'],
        description: 'Copies a set of flags from staging to prod for a release window',
        tools_used: 'Feature Flags',
        flow: 'feature-flag-get-all, feature-flags-copy-flags-create, feature-flags-bulk-keys-create',
        example_prompts: [
            'Copy these flags from staging to prod for the release',
            'Promote our staging flag config to production',
            'Sync flags across projects',
        ],
    },
    {
        name: 'Manage personal LLM spend',
        stage: 'Scale',
        tags: ['Engineering', 'Finance'],
        description: 'Surfaces personal LLM spend and trends in a notebook for awareness',
        tools_used: 'LLM Observability',
        flow: 'llma-personal-spend, get-llm-total-costs-for-project, notebooks-create',
        example_prompts: [
            'How much am I personally spending on LLM calls?',
            'Summarize my LLM spend trend in a notebook',
            'Break down my AI usage this month',
        ],
    },
    {
        name: 'Sentiment-score LLM transcripts',
        stage: 'Scale',
        tags: ['Engineering', 'Product Management'],
        description: 'Runs sentiment scoring across recent LLM traces and surfaces negative segments',
        tools_used: 'LLM Observability',
        flow: 'llma-sentiment-create, llma-summarization-create, query-llm-traces-list',
        example_prompts: [
            'Sentiment-score recent assistant conversations and surface the unhappy ones',
            'Where are users frustrated with the AI?',
            'Find negative-sentiment LLM transcripts',
        ],
    },
    {
        name: 'Set up persistent eval keys',
        stage: 'Enterprise',
        tags: ['Engineering'],
        description: 'Configures the active evaluation key and reusable config for the team',
        tools_used: 'LLM Observability',
        flow: 'llma-evaluation-config-get, llma-evaluation-config-set-active-key',
        example_prompts: [
            'Set the team’s active evaluation key and reusable config',
            'Configure persistent eval keys so everyone shares one setup',
            'Make our eval config the default',
        ],
    },
    {
        name: 'Investigate a single LLM error',
        stage: 'Scale',
        tags: ['Engineering'],
        description: 'Locates a failing LLM trace and reviews the prompt, response, and error span',
        tools_used: 'LLM Observability, APM/Tracing',
        flow: 'query-llm-traces-list, query-llm-trace, apm-trace-get',
        example_prompts: [
            'Find the failing LLM call and show me the prompt, response, and error',
            'Why did this generation error out?',
            'Debug this LLM trace’s failure',
        ],
    },
    {
        name: 'Run a search across all entities',
        stage: 'Scale',
        tags: ['Ops', 'Product Management'],
        description: 'Searches across insights, dashboards, flags, and experiments for a keyword',
        tools_used: 'Search',
        flow: 'entity-search',
        example_prompts: [
            'Search everything for “checkout” — insights, dashboards, flags, experiments',
            'Where is “activation” used across the project?',
            'Find anything related to pricing',
        ],
    },
    {
        name: 'Investigate why a query timed out',
        stage: 'Scale',
        tags: ['Data', 'Engineering'],
        description: 'Validates and explains a HogQL query that timed out and proposes optimizations',
        tools_used: 'SQL',
        flow: 'query-validate, hogql-schema, execute-sql',
        example_prompts: [
            'This query timed out — explain why and propose a faster version',
            'Optimize the HogQL that’s hanging',
            'Why is this insight failing to load?',
        ],
    },
    {
        name: 'Detect a tracking SDK drift',
        stage: 'Enterprise',
        tags: ['Engineering', 'Data'],
        description: 'Compares SDK versions in use against expected and flags drift',
        tools_used: 'SDK Doctor, Properties',
        flow: 'sdk-doctor-get, properties-list',
        example_prompts: [
            'Are clients drifting off our expected SDK version?',
            'Flag where the SDK in the wild differs from what we ship',
            'Detect tracking drift across our apps',
        ],
    },
    {
        name: 'Create a privacy delete request',
        stage: 'Enterprise',
        tags: ['Security', 'Support'],
        description: 'Bulk-deletes a person and their sessions for a privacy request',
        tools_used: 'Persons, Session Replay',
        flow: 'persons-list, persons-bulk-delete, session-recording-delete',
        example_prompts: [
            'Handle this GDPR request: delete the person and all their recordings',
            'Erase everything we have on this user',
            'Process a right-to-be-forgotten request end to end',
        ],
    },
    {
        name: 'Investigate a comment thread for context',
        stage: 'Scale',
        tags: ['Product Management', 'Engineering'],
        description: 'Walks comment threads on a dashboard to understand recent decisions',
        tools_used: 'Comments',
        flow: 'comments-list, comment-thread, comment-get',
        example_prompts: [
            'Catch me up on the discussion around this dashboard',
            'What did the team decide in these comment threads?',
            'Summarize the context behind this metric’s comments',
        ],
    },
    {
        name: 'Tag insights by team',
        stage: 'Enterprise',
        tags: ['Ops', 'Data'],
        description: 'Bulk-tags insights by team for the new analytics governance model',
        tools_used: 'Insights',
        flow: 'insights-list, insight-update, insight-get',
        example_prompts: [
            'Tag every insight by owning team for governance',
            'Bulk-label our insights by department',
            'Organize insights so each team finds its own',
        ],
    },
    {
        name: 'Investigate a region-specific spike',
        stage: 'Enterprise',
        tags: ['Engineering', 'Ops'],
        description: 'Looks at logs, errors, and APM across regions to localize a problem',
        tools_used: 'Logs, APM/Tracing, Error Tracking',
        flow: 'query-logs, apm-spans-aggregate, query-error-tracking-issues-list',
        example_prompts: [
            'Errors spiked in eu-west — localize the problem across logs, APM, and issues',
            'Is this incident region-specific?',
            'Why is one region degraded and not the others?',
        ],
    },
    {
        name: 'Detect a slow third-party dependency',
        stage: 'Enterprise',
        tags: ['Engineering'],
        description: 'Slices APM spans by upstream service to find a slow dependency',
        tools_used: 'APM/Tracing',
        flow: 'apm-attributes-list, apm-attribute-values-list, apm-spans-aggregate',
        example_prompts: [
            'Which third-party dependency is dragging down our latency?',
            'Slice spans by upstream service to find the slow one',
            'Is Stripe or our DB the bottleneck here?',
        ],
    },
    {
        name: 'Onboard a new team to PostHog',
        stage: 'Enterprise',
        tags: ['Ops'],
        description: 'Creates roles, dashboards, and starter insights for a new internal team',
        tools_used: 'Org & Project Management, Dashboards',
        flow: 'roles-list, dashboard-create, insight-create',
        example_prompts: [
            'Onboard the new marketing team: roles, a dashboard, and starter insights',
            'Set up a new internal team in PostHog',
            'Give the design team their own space and metrics',
        ],
    },
    {
        name: "Build a 'biggest movers' dashboard",
        stage: 'Scale',
        tags: ['Product Management'],
        description: 'Surfaces metrics with the biggest week-over-week movement automatically',
        tools_used: 'Insights',
        flow: 'insights-trending-retrieve, insights-list, insight-create',
        example_prompts: [
            'Build a dashboard that always shows our biggest week-over-week movers',
            'What changed most this week, automatically?',
            'Surface the metrics moving fastest',
        ],
    },
    {
        name: 'Confirm an experiment is statistically valid',
        stage: 'Scale',
        tags: ['Data', 'Product Management'],
        description: 'Checks stats and timeseries for an experiment to confirm validity',
        tools_used: 'Experiments',
        flow: 'experiment-stats, experiment-timeseries-results, experiment-results-get',
        example_prompts: [
            'Is this experiment actually significant, or are we fooling ourselves?',
            'Check the stats and time series before we call this a win',
            'Confirm this test is valid before we ship',
        ],
    },
    {
        name: 'Discover hidden user interview themes',
        stage: 'Scale',
        tags: ['Product Management', 'Design'],
        description: 'Searches user interviews for repeated language to find unspoken themes',
        tools_used: 'User Interviews',
        flow: 'user-interviews-search, user-interviews-retrieve, notebooks-create',
        example_prompts: [
            'What themes keep coming up across our interviews that we haven’t named?',
            'Search interviews for repeated language and surface hidden patterns',
            'Find the unspoken needs in our research',
        ],
    },
    {
        name: 'Update a person CSV file',
        stage: 'Enterprise',
        tags: ['Ops'],
        description: 'Generates a CSV of links for interview invitees and tracks responses',
        tools_used: 'User Interviews',
        flow: 'user-interview-topics-generate-links, user-interview-topics-links-csv',
        example_prompts: [
            'Generate booking links for these invitees and export a CSV',
            'Give me a CSV of interview links to send out',
            'Export scheduling links for the recruiting list',
        ],
    },
    {
        name: 'Set up visual regression review',
        stage: 'Enterprise',
        tags: ['Design', 'Engineering'],
        description: 'Configures visual review runs and tolerated hashes for the design system',
        tools_used: 'Visual Review',
        flow: 'visual-review-repos-list, visual-review-runs-list, visual-review-runs-tolerate-create',
        example_prompts: [
            'Set up visual regression review for our design system',
            'Configure tolerated hashes so intentional changes don’t flag',
            'Wire up visual review for this repo',
        ],
    },
    {
        name: 'Approve a visual review run',
        stage: 'Enterprise',
        tags: ['Design'],
        description: 'Reviews snapshots from a visual review run and approves the new baseline',
        tools_used: 'Visual Review',
        flow: 'visual-review-runs-list, visual-review-runs-snapshots-list, visual-review-runs-approve-create',
        example_prompts: [
            'Review this run’s snapshots and approve the new baseline',
            'Are these visual diffs intentional? Approve if so',
            'Sign off on the latest visual review',
        ],
    },
    {
        name: 'Inspect visual review run history',
        stage: 'Enterprise',
        tags: ['Design', 'Engineering'],
        description: 'Looks at snapshot history for a regression and decides on tolerance',
        tools_used: 'Visual Review',
        flow: 'visual-review-runs-retrieve, visual-review-runs-snapshot-history-list, visual-review-runs-tolerated-hashes-list',
        example_prompts: [
            'When did this component’s snapshot first change?',
            'Walk the history of this visual regression',
            'Decide whether to tolerate this recurring diff',
        ],
    },
    {
        name: "Build a 'rage clicks by URL' heatmap",
        stage: 'Scale',
        tags: ['Design', 'Product Management'],
        description: 'Aggregates rage clicks by URL and visualizes the worst pages',
        tools_used: 'Web Analytics, Session Replay',
        flow: 'query-trends, query-session-recordings-list, insight-create',
        example_prompts: [
            'Rank pages by rage clicks and pull session recordings for the worst',
            'Which URLs frustrate users most?',
            'Build a rage-clicks-by-page insight',
        ],
    },
    {
        name: "Surface 'first time user' recordings",
        stage: 'Startup',
        tags: ['Founder', 'Design'],
        description: 'Curates recordings from brand-new signups for daily founder watching',
        tools_used: 'Session Replay, Cohorts',
        flow: 'cohorts-create, query-session-recordings-list, session-recording-playlist-create',
        example_prompts: [
            'Curate recordings of brand-new signups for me to watch daily',
            'Show me first-time users’ very first sessions',
            'Build a first-run replay playlist',
        ],
    },
    {
        name: 'Detect a botted signup wave',
        stage: 'Scale',
        tags: ['Security', 'Engineering'],
        description: 'Finds suspiciously similar signup patterns and quarantines the affected persons',
        tools_used: 'Persons, Product Analytics',
        flow: 'persons-list, query-trends, persons-bulk-delete',
        example_prompts: [
            'Signups spiked weirdly — find the bot pattern and quarantine them',
            'Are these new accounts real or scripted?',
            'Detect and remove a botted signup wave',
        ],
    },
    {
        name: 'Validate the data warehouse schema',
        stage: 'Enterprise',
        tags: ['Data'],
        description: 'Reads the warehouse schema and verifies new tables match expectations',
        tools_used: 'Data Warehouse',
        flow: 'read-data-warehouse-schema, view-list, view-get',
        example_prompts: [
            'Verify the new tables landed with the expected schema',
            'Check the warehouse schema after the migration',
            'Do our views match what we expect?',
        ],
    },
    {
        name: "Create a recurring 'top user actions' insight",
        stage: 'Scale',
        tags: ['Product Management'],
        description: 'Tracks the most common user actions per week and pins them to the home view',
        tools_used: 'Insights, Dashboards',
        flow: 'insight-create, dashboard-create, query-trends',
        example_prompts: [
            'Track the most common user actions each week and pin it home',
            'What do users do most, refreshed weekly?',
            'Build a top-actions insight',
        ],
    },
    {
        name: 'Find users who hit a paywall and converted',
        stage: 'Scale',
        tags: ['Billing', 'Growth'],
        description: 'Builds a cohort of users who saw a paywall and converted within 24h',
        tools_used: 'Cohorts, Product Analytics',
        flow: 'cohorts-create, query-funnel, query-trends',
        example_prompts: [
            'Build a cohort of users who hit the paywall and upgraded within a day',
            'Who converts fastest after seeing the paywall?',
            'Measure paywall-to-upgrade within 24 hours',
        ],
    },
    {
        name: 'Compare two SDK versions in the wild',
        stage: 'Enterprise',
        tags: ['Engineering'],
        description: 'Splits sessions by SDK version and looks for error or latency deltas',
        tools_used: 'SDK Doctor, Session Replay, Error Tracking',
        flow: 'sdk-doctor-get, query-error-tracking-issues-list, query-session-recordings-list',
        example_prompts: [
            'Does the new SDK version have more errors than the old one?',
            'Split sessions by SDK version and compare error rates',
            'Is it safe to push everyone to the latest SDK?',
        ],
    },
    {
        name: 'Map a noisy log attribute',
        stage: 'Enterprise',
        tags: ['Engineering', 'Data'],
        description: 'Identifies a log attribute with high cardinality and reduces noise',
        tools_used: 'Logs',
        flow: 'logs-attributes-list, logs-attribute-values-list, query-logs',
        example_prompts: [
            'Which log attribute has runaway cardinality? Help me tame it',
            'Find the noisy field blowing up our log volume',
            'Reduce noise from this high-cardinality attribute',
        ],
    },
    {
        name: 'Decommission a deprecated service',
        stage: 'Enterprise',
        tags: ['Engineering'],
        description: 'Confirms a service has zero traffic via APM and proceeds with decommissioning',
        tools_used: 'APM/Tracing',
        flow: 'apm-services-list, apm-spans-aggregate, query-apm-spans',
        example_prompts: [
            'Confirm this service gets zero traffic so we can decommission it',
            'Is anything still calling the legacy service?',
            'Verify it’s safe to shut down before I delete it',
        ],
    },
    {
        name: 'Sample a few support sessions',
        stage: 'MVP',
        tags: ['Support'],
        description: 'Samples 5 random support recordings for a quality check',
        tools_used: 'Session Replay',
        flow: 'query-session-recordings-list, session-recording-get, session-recording-summarize',
        example_prompts: [
            'Pull 5 random support sessions for a quality check',
            'Sample some recent sessions so I can spot-check the experience',
            'Show me a handful of session recordings to review',
        ],
    },
    {
        name: 'Plan an offsite retro',
        stage: 'Startup',
        tags: ['Founder', 'Ops'],
        description: 'Pulls quarterly metrics, key incidents, and customer wins into a single notebook',
        tools_used: 'Notebooks, Annotations, Insights',
        flow: 'notebooks-create, annotations-list, insights-list',
        example_prompts: [
            'Build an offsite retro notebook: quarterly metrics, incidents, and wins',
            'Prep our quarter-in-review for the team offsite',
            'Pull the highlights and lowlights of the quarter',
        ],
    },
    {
        name: 'Spin up an MVP error tracker',
        stage: 'MVP',
        tags: ['Engineering'],
        description: 'Wires error tracking for the first time and reviews the first day of issues',
        tools_used: 'Error Tracking',
        flow: 'query-error-tracking-issues-list, error-tracking-grouping-rules-create, error-tracking-grouping-rules-list',
        example_prompts: [
            'Set up error tracking and walk me through the first day of issues',
            'Turn on error monitoring for my MVP',
            'What’s breaking now that I’ve enabled error tracking?',
        ],
    },
]
